import React, { useState, useEffect } from "react"
import Navigation from "../components/Navigation"
import Header from "../components/Header"
import NotificationToaster from "../components/NotificationToaster"
import Login from "../components/Login"
import LogoutConfirmModal from "../components/LogoutConfirmModal"
import { useData } from "../contexts/DataContext"
import useLogout from "../hooks/useLogout"
import {
    componentServices,
    groupReportServices,
    propertyServices,
    excelImportServices,
    utils
} from "../services/componentServices"

const currencies = [
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham (Dubai)' },
    { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
    { code: 'QAR', symbol: '﷼', name: 'Qatari Riyal' },
    { code: 'KWD', symbol: 'د.ك', name: 'Kuwaiti Dinar' },
    { code: 'BHD', symbol: '.د.ب', name: 'Bahraini Dinar' },
    { code: 'OMR', symbol: '﷼', name: 'Omani Rial' }
];

const ComponentsPage = ({
    currentUser,
    onLogout,
    onShowUserManagement,
    onTabChange,
    activeTab,
    sidebarOpen,
    onToggleSidebar,
}) => {
    const { savedComponents } = useData()
    const [notifications, setNotifications] = useState([])
    const [searchTerm, setSearchTerm] = useState("")
    const [sortBy, setSortBy] = useState("oldest")
    const [shouldRefresh, setShouldRefresh] = useState(false)
    const [showGlobalReport, setShowGlobalReport] = useState(true)
    const [showGroupReport, setShowGroupReport] = useState(false)
    const [currency, setCurrency] = useState(localStorage.getItem('blendOptimizerCurrency') || "INR")
    const [filters, setFilters] = useState({
        minCost: "",
        maxCost: "",
        minGravity: "",
        maxGravity: "",
    })
    const [showAddGroupPopup, setShowAddGroupPopup] = useState(false)
    const [selectedComponents, setSelectedComponents] = useState([])
    const [groupName, setGroupName] = useState("")
    const [groupDescription, setGroupDescription] = useState("")
    const [savedGroups, setSavedGroups] = useState([])
    const [file, setFile] = useState(null)
    const [loading, setLoading] = useState(false)
    const [apiComponents, setApiComponents] = useState([])
    const [loadingComponents, setLoadingComponents] = useState(false)
    const [apiGroupReports, setApiGroupReports] = useState([])
    const [loadingGroupReports, setLoadingGroupReports] = useState(false)
    const [addedComponents, setAddedComponents] = useState([])
    const [loadingAddedComponents, setLoadingAddedComponents] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [showAddComponentModal, setShowAddComponentModal] = useState(false)
    const [newComponent, setNewComponent] = useState({
        name: "",
        cost: "",
        costUnit: localStorage.getItem('blendOptimizerCurrency') || "INR",
        gravity: "",
        minVolume: "",
        maxVolume: "",
        availableVolume: "",
        volumeUnit: "L",
        density: "",
        selectedProperties: [],
        selectedGroups: [],
    })
    const [propertyType, setPropertyType] = useState("global")
    const [globalPropertyOptions, setGlobalPropertyOptions] = useState([])
    const [groupPropertyOptions, setGroupPropertyOptions] = useState([])
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [componentToDelete, setComponentToDelete] = useState(null)
    const [expandedGroupReport, setExpandedGroupReport] = useState(null)
    const [importedComponents, setImportedComponents] = useState([])
    const [showExcelImportPopup, setShowExcelImportPopup] = useState(false)
    const [globalProperties, setGlobalProperties] = useState([])
    const [propertyGroupsFromAPI, setPropertyGroupsFromAPI] = useState([])
    const [propertySetsFromAPI, setPropertySetsFromAPI] = useState([])
    const [loadingProperties, setLoadingProperties] = useState(false)
    const [excelPreviewData, setExcelPreviewData] = useState([])
    const [selectedPreviewComponents, setSelectedPreviewComponents] = useState([])
    const [showExcelPreview, setShowExcelPreview] = useState(false)
    const [editingComponent, setEditingComponent] = useState(null)
    const [showEditComponentPopup, setShowEditComponentPopup] = useState(false)
    const [editedComponentData, setEditedComponentData] = useState({})
    const [globalLoading, setGlobalLoading] = useState(false)
    const [showEditGroupPopup, setShowEditGroupPopup] = useState(false)
    const [editingGroup, setEditingGroup] = useState(null)
    const [groupComponentsToAdd, setGroupComponentsToAdd] = useState([])
    const [editingGroupName, setEditingGroupName] = useState("") // New state for editing group name

    const [selectedForBulkDelete, setSelectedForBulkDelete] = useState([])
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)
    const [excelData, setExcelData] = useState([]);
    const [showExcelModal, setShowExcelModal] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const [selectedExcelComponents, setSelectedExcelComponents] = useState([]);

    const {
        showLogoutConfirm,
        handleLogoutClick,
        handleConfirmLogout,
        handleCancelLogout
    } = useLogout()

    // const fetchComponents = async () => {
    //     setLoadingComponents(true);
    //     try {
    //         const components = await componentServices.fetchComponents();
    //         // Sort components by creation date (oldest first)
    //         const sortedComponents = components.sort((a, b) => 
    //             new Date(a.createdAt || a.timestamp) - new Date(b.createdAt || b.timestamp)
    //         );
    //         setApiComponents(sortedComponents);
    //     } catch (error) {
    //         console.error("Error fetching components:", error);
    //         setApiComponents([]);
    //     } finally {
    //         setLoadingComponents(false);
    //     }
    // };


    const fetchComponents = async () => {
        setLoadingComponents(true);
        try {
            const components = await componentServices.fetchComponents();
            // Sort components by creation date (oldest first - new components appear at bottom)
            const sortedComponents = components.sort((a, b) =>
                new Date(a.createdAt || a.timestamp) - new Date(b.createdAt || b.timestamp)
            );
            setApiComponents(sortedComponents);
        } catch (error) {
            console.error("Error fetching components:", error);
            setApiComponents([]);
        } finally {
            setLoadingComponents(false);
        }
    };

    // const fetchAddedComponents = async () => {
    //     setLoadingAddedComponents(true);
    //     try {
    //         const components = await componentServices.fetchAddedComponents();
    //         // Sort added components by creation date (oldest first)
    //         const sortedComponents = (components || []).sort((a, b) => 
    //             new Date(a.createdAt || a.timestamp) - new Date(b.createdAt || b.timestamp)
    //         );
    //         setAddedComponents(sortedComponents);
    //     } catch (error) {
    //         console.error("Error fetching added components:", error);
    //         setAddedComponents([]);
    //     } finally {
    //         setLoadingAddedComponents(false);
    //     }
    // };



    const fetchAddedComponents = async () => {
        setLoadingAddedComponents(true);
        try {
            const components = await componentServices.fetchAddedComponents();
            // Sort added components by creation date (oldest first - new components appear at bottom)
            const sortedComponents = (components || []).sort((a, b) =>
                new Date(a.createdAt || a.timestamp) - new Date(b.createdAt || b.timestamp)
            );
            setAddedComponents(sortedComponents);
        } catch (error) {
            console.error("Error fetching added components:", error);
            setAddedComponents([]);
        } finally {
            setLoadingAddedComponents(false);
        }
    };


    const fetchGroupReports = async () => {
        setLoadingGroupReports(true);
        try {
            const reports = await groupReportServices.fetchGroupReports();
            setApiGroupReports(reports);
        } catch (error) {
            console.error("Error fetching group reports:", error);
            addNotification("error", `Failed to load group reports: ${error.message}`);
        } finally {
            setLoadingGroupReports(false);
        }
    };

    const fetchPropertiesFromAPIs = async () => {
        setLoadingProperties(true);
        try {
            const properties = await propertyServices.fetchAllProperties();
            setGlobalProperties(properties.globalProperties);
            setPropertyGroupsFromAPI(properties.propertyGroups);
            setPropertySetsFromAPI(properties.propertySets);
        } catch (error) {
            console.error("Error fetching properties:", error);
        } finally {
            setLoadingProperties(false);
        }
    };

    const saveComponentToMongoDB = async (componentData) => {
        return await componentServices.saveComponent(componentData);
    };

    const updateComponentInMongoDB = async (id, componentData) => {
        return await componentServices.updateComponent(id, componentData);
    };

    const deleteComponentFromMongoDB = async (id) => {
        return await componentServices.deleteComponent(id);
    };

    const createAddedComponent = async (componentData) => {
        return await componentServices.createAddedComponent(componentData);
    };

    const updateAddedComponent = async (id, componentData) => {
        return await componentServices.updateAddedComponent(id, componentData);
    };

    const deleteAddedComponent = async (id) => {
        return await componentServices.deleteAddedComponent(id);
    };

    const createGroupReportFromGroup = async (groupData) => {
        return await groupReportServices.createGroupReport(groupData);
    };

    const updateGroupReport = async (id, groupData) => {
        return await groupReportServices.updateGroupReport(id, groupData);
    };

    const deleteGroupReport = async (id) => {
        return await groupReportServices.deleteGroupReport(id);
    };

    const onChooseFile = (e) => {
        const selectedFile = e.target.files?.[0] || null;
        setFile(selectedFile);
        if (selectedFile) {
            addNotification("info", `Selected file: ${selectedFile.name}`);
            setExcelPreviewData([]);
            setShowExcelPreview(false);
        }
    };

    const loadExcelForPreview = async () => {
        if (!file) {
            addNotification("error", "Please choose an Excel file first");
            return;
        }

        setLoading(true);
        try {
            const previewComponents = await excelImportServices.processExcelForPreview(file);
            setExcelPreviewData(previewComponents);
            setSelectedPreviewComponents(previewComponents.map(comp => comp.id));
            setShowExcelPreview(true);
            addNotification("success", `Loaded ${previewComponents.length} components for preview`);
        } catch (error) {
            console.error("Error loading Excel:", error);
            addNotification("error", `Failed to process Excel file: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const importPreviewedComponents = async () => {
        const selectedComponents = excelPreviewData.filter(comp =>
            selectedPreviewComponents.includes(comp.id)
        );

        if (selectedComponents.length === 0) {
            addNotification("error", "Please select at least one component to import");
            return;
        }

        setLoading(true);
        try {
            const { importedComponents, importedCount, errorCount } = await excelImportServices.importComponents(selectedComponents);

            if (importedComponents.length > 0) {
                const existingComponents = JSON.parse(localStorage.getItem('addedComponents') || '[]');
                // Add imported components to the end (bottom)
                const updatedComponents = [...existingComponents, ...importedComponents];
                localStorage.setItem('addedComponents', JSON.stringify(updatedComponents));

                window.dispatchEvent(new CustomEvent('componentsUpdated', {
                    detail: {
                        addedComponents: updatedComponents,
                        type: 'excel-imported'
                    }
                }));
            }

            addNotification("success", `Successfully imported ${importedCount} components to database`);

            if (errorCount > 0) {
                addNotification("warning", `${errorCount} components failed to import`);
            }

            setFile(null);
            setExcelPreviewData([]);
            setSelectedPreviewComponents([]);
            setShowExcelPreview(false);
            setShowExcelImportPopup(false);

            const fileInput = document.getElementById('excel-file-components-popup');
            if (fileInput) fileInput.value = '';

            setShouldRefresh(true);

            await fetchComponents();
            await fetchAddedComponents();

        } catch (error) {
            console.error("Error importing components:", error);
            addNotification("error", `Failed to import components: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const getCurrencySymbol = (currCode = null) => {
        return utils.getCurrencySymbol(currCode || currency);
    };

    const generateUniqueKey = (component, index, type) => {
        return utils.generateUniqueKey(component, index, type);
    };

    const getSelectedPropertyNames = (component) => {
        return utils.getSelectedPropertyNames(component);
    };

    const addNotification = (type, message) => {
        const id = Date.now()
        setNotifications((prev) => [...prev, { id, type, message }])

        setTimeout(() => {
            setNotifications((prev) => prev.filter((n) => n.id !== id))
        }, 3000)
    }

    useEffect(() => {
        const savedCurrency = localStorage.getItem("blendOptimizerCurrency")
        if (savedCurrency) {
            setCurrency(savedCurrency)
        }

        fetchComponents();
        fetchGroupReports();
        fetchAddedComponents();
        fetchPropertiesFromAPIs();
    }, [currentUser, shouldRefresh])

    useEffect(() => {
        setCurrentPage(1)
    }, [searchTerm, sortBy, filters, showGlobalReport, showGroupReport])

    useEffect(() => {
        if (showAddComponentModal) {
            loadPropertyAndGroupOptions()
        }
    }, [showAddComponentModal])

    // const loadPropertyAndGroupOptions = () => {
    //     const { globalPropertyOptions: globalOptions, groupPropertyOptions: groupOptions } = 
    //         utils.preparePropertyOptions(globalProperties, propertyGroupsFromAPI);

    //     setGlobalPropertyOptions(globalOptions);
    //     setGroupPropertyOptions(groupOptions);
    // }


    const loadPropertyAndGroupOptions = () => {
        const { globalPropertyOptions: globalOptions, groupPropertyOptions: groupOptions } =
            utils.preparePropertyOptions(globalProperties, propertyGroupsFromAPI);

        // Force 0 as default value instead of 50
        const processedGlobalOptions = globalOptions.map(prop => ({
            ...prop,
            defaultValue: prop.defaultValue === 50 ? 0 : prop.defaultValue
        }));

        const processedGroupOptions = groupOptions.map(group => ({
            ...group,
            defaultValue: group.defaultValue === 50 ? 0 : group.defaultValue
        }));

        setGlobalPropertyOptions(processedGlobalOptions);
        setGroupPropertyOptions(processedGroupOptions);
    }

    // const handlePropertySelection = (item) => {
    //     if (propertyType === "global") {
    //         setNewComponent((prev) => {
    //             const isSelected = prev.selectedProperties.some(p => p.name === item.name)
    //             if (isSelected) {
    //                 return {
    //                     ...prev,
    //                     selectedProperties: prev.selectedProperties.filter(p => p.name !== item.name)
    //                 }
    //             } else {
    //                 return {
    //                     ...prev,
    //                     selectedProperties: [...prev.selectedProperties, {
    //                         ...item,
    //                         editableDefaultValue: item.defaultValue
    //                     }]
    //                 }
    //             }
    //         })
    //     } else {
    //         setNewComponent((prev) => {
    //             const isSelected = prev.selectedGroups.some(g => g.name === item.name)
    //             if (isSelected) {
    //                 return {
    //                     ...prev,
    //                     selectedGroups: prev.selectedGroups.filter(g => g.name !== item.name)
    //                 }
    //             } else {
    //                 return {
    //                     ...prev,
    //                     selectedGroups: [...prev.selectedGroups, {
    //                         ...item,
    //                         editableDefaultValue: item.defaultValue
    //                     }]
    //                 }
    //             }
    //         })
    //     }
    // }

    const handlePropertySelection = (item) => {
        if (propertyType === "global") {
            setNewComponent((prev) => {
                const isSelected = prev.selectedProperties.some(p => p.name === item.name)
                if (isSelected) {
                    return {
                        ...prev,
                        selectedProperties: prev.selectedProperties.filter(p => p.name !== item.name)
                    }
                } else {
                    return {
                        ...prev,
                        selectedProperties: [...prev.selectedProperties, {
                            ...item,
                            editableDefaultValue: 0 // Force 0 instead of item.defaultValue
                        }]
                    }
                }
            })
        } else {
            setNewComponent((prev) => {
                const isSelected = prev.selectedGroups.some(g => g.name === item.name)
                if (isSelected) {
                    return {
                        ...prev,
                        selectedGroups: prev.selectedGroups.filter(g => g.name !== item.name)
                    }
                } else {
                    return {
                        ...prev,
                        selectedGroups: [...prev.selectedGroups, {
                            ...item,
                            editableDefaultValue: 0 // Force 0 instead of item.defaultValue
                        }]
                    }
                }
            })
        }
    }

    const handleDefaultValueChange = (itemName, newValue, isGlobal = true) => {
        if (isGlobal) {
            setNewComponent(prev => ({
                ...prev,
                selectedProperties: prev.selectedProperties.map(prop =>
                    prop.name === itemName
                        ? { ...prop, editableDefaultValue: newValue }
                        : prop
                )
            }));
        } else {
            setNewComponent(prev => ({
                ...prev,
                selectedGroups: prev.selectedGroups.map(group =>
                    group.name === itemName
                        ? { ...group, editableDefaultValue: newValue }
                        : group
                )
            }));
        }
    }

    // const handleSelectAllProperties = () => {
    //     if (propertyType === "global") {
    //         setNewComponent((prev) => {
    //             if (prev.selectedProperties.length === globalPropertyOptions.length) {
    //                 return { ...prev, selectedProperties: [] }
    //             } else {
    //                 return {
    //                     ...prev,
    //                     selectedProperties: globalPropertyOptions.map(prop => ({
    //                         ...prop,
    //                         editableDefaultValue: prop.defaultValue
    //                     }))
    //                 }
    //             }
    //         })
    //     } else {
    //         setNewComponent((prev) => {
    //             if (prev.selectedGroups.length === groupPropertyOptions.length) {
    //                 return { ...prev, selectedGroups: [] }
    //             } else {
    //                 return {
    //                     ...prev,
    //                     selectedGroups: groupPropertyOptions.map(group => ({
    //                         ...group,
    //                         editableDefaultValue: group.defaultValue
    //                     }))
    //                 }
    //             }
    //         })
    //     }
    // }


    const handleSelectAllProperties = () => {
        if (propertyType === "global") {
            setNewComponent((prev) => {
                if (prev.selectedProperties.length === globalPropertyOptions.length) {
                    return { ...prev, selectedProperties: [] }
                } else {
                    return {
                        ...prev,
                        selectedProperties: globalPropertyOptions.map(prop => ({
                            ...prop,
                            editableDefaultValue: 0 // Force 0
                        }))
                    }
                }
            })
        } else {
            setNewComponent((prev) => {
                if (prev.selectedGroups.length === groupPropertyOptions.length) {
                    return { ...prev, selectedGroups: [] }
                } else {
                    return {
                        ...prev,
                        selectedGroups: groupPropertyOptions.map(group => ({
                            ...group,
                            editableDefaultValue: 0 // Force 0
                        }))
                    }
                }
            })
        }
    }

    // const handleAddComponent = async () => {
    //     if (!newComponent.name.trim()) {
    //         addNotification("error", "Component name is required")
    //         return
    //     }

    //     if (newComponent.selectedProperties.length === 0 && newComponent.selectedGroups.length === 0) {
    //         addNotification("error", "Please select at least one property or group")
    //         return
    //     }

    //     try {
    //         const allSelectedItems = [...newComponent.selectedProperties, ...newComponent.selectedGroups];
    //         const selectedPropertyText = allSelectedItems.map(item =>
    //             `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || item.defaultValue}`
    //         ).join("; ");

    //         const gravityValue = newComponent.gravity !== "" ? Number(newComponent.gravity) : 0;

    //         const componentData = {
    //             name: newComponent.name.trim(),
    //             cost: newComponent.cost ? Number(newComponent.cost) : 0,
    //             costUnit: newComponent.costUnit,
    //             gravity: gravityValue, 
    //             selectedProperty: selectedPropertyText,
    //             selectedProperties: newComponent.selectedProperties.map(prop => ({
    //                 ...prop,
    //                 defaultValue: prop.editableDefaultValue || prop.defaultValue
    //             })),
    //             selectedGroups: newComponent.selectedGroups.map(group => ({
    //                 ...group,
    //                 defaultValue: group.editableDefaultValue || group.defaultValue
    //             })),
    //             source: "manual",
    //             minVolume: newComponent.minVolume ? Number(newComponent.minVolume) : 0,
    //             maxVolume: newComponent.maxVolume ? Number(newComponent.maxVolume) : 1000,
    //             availableVolume: newComponent.availableVolume ? Number(newComponent.availableVolume) : 500,
    //             volumeUnit: newComponent.volumeUnit,
    //             density: newComponent.density ? Number(newComponent.density) : 0.85,
    //         }

    //         console.log("Saving component with gravity:", gravityValue); 

    //         // Save to MongoDB
    //         const result = await saveComponentToMongoDB(componentData);

    //         if (result.success) {
    //             addNotification("success", `Component "${componentData.name}" added successfully to database`);

    //             const existingComponents = JSON.parse(localStorage.getItem('addedComponents') || '[]');
    //             // Add new component to the end (bottom) of the list
    //             const updatedComponents = [...existingComponents, {
    //                 ...componentData,
    //                 _id: result.data?._id || `local-${Date.now()}`,
    //                 id: result.data?._id || `local-${Date.now()}`,
    //                 createdAt: new Date().toISOString(),
    //                 source: 'manual',
    //                 sourceDisplay: 'Added Component'
    //             }];
    //             localStorage.setItem('addedComponents', JSON.stringify(updatedComponents));

    //             window.dispatchEvent(new CustomEvent('componentsUpdated', {
    //                 detail: {
    //                     addedComponents: updatedComponents,
    //                     type: 'added'
    //                 }
    //             }));

    //             await fetchComponents();
    //             await fetchAddedComponents();

    //             setShowAddComponentModal(false);
    //             setNewComponent({
    //                 name: "",
    //                 cost: "",
    //                 costUnit: "INR",
    //                 gravity: "",
    //                 minVolume: "",
    //                 maxVolume: "",
    //                 availableVolume: "",
    //                 volumeUnit: "L",
    //                 density: "",
    //                 selectedProperties: [],
    //                 selectedGroups: [],
    //             });
    //             setPropertyType("global");
    //             setShouldRefresh(true);
    //         }
    //     } catch (error) {
    //         console.error("Error adding component:", error);
    //         addNotification("error", `Failed to add component: ${error.message}`);
    //     }
    // }


    const handleAddComponent = async () => {
        if (!newComponent.name.trim()) {
            addNotification("error", "Component name is required")
            return
        }

        if (newComponent.selectedProperties.length === 0 && newComponent.selectedGroups.length === 0) {
            addNotification("error", "Please select at least one property or group")
            return
        }

        try {
            const allSelectedItems = [...newComponent.selectedProperties, ...newComponent.selectedGroups];
            const selectedPropertyText = allSelectedItems.map(item =>
                `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || 0}`
            ).join("; ");

            const gravityValue = newComponent.gravity !== "" ? Number(newComponent.gravity) : 0;

            const componentData = {
                name: newComponent.name.trim(),
                cost: newComponent.cost ? Number(newComponent.cost) : 0,
                costUnit: newComponent.costUnit,
                gravity: gravityValue,
                selectedProperty: selectedPropertyText,
                selectedProperties: newComponent.selectedProperties.map(prop => ({
                    ...prop,
                    defaultValue: prop.editableDefaultValue || 0 // Force 0
                })),
                selectedGroups: newComponent.selectedGroups.map(group => ({
                    ...group,
                    defaultValue: group.editableDefaultValue || 0 // Force 0
                })),
                source: "manual",
                minVolume: newComponent.minVolume ? Number(newComponent.minVolume) : 0,
                maxVolume: newComponent.maxVolume ? Number(newComponent.maxVolume) : 1000,
                availableVolume: newComponent.availableVolume ? Number(newComponent.availableVolume) : 500,
                volumeUnit: newComponent.volumeUnit,
                density: newComponent.density ? Number(newComponent.density) : 0,
            }

            console.log("Saving component with gravity:", gravityValue);

            // Save to MongoDB
            const result = await saveComponentToMongoDB(componentData);

            if (result.success) {
                addNotification("success", `Component "${componentData.name}" added successfully to database`);

                const existingComponents = JSON.parse(localStorage.getItem('addedComponents') || '[]');
                const updatedComponents = [...existingComponents, {
                    ...componentData,
                    _id: result.data?._id || `local-${Date.now()}`,
                    id: result.data?._id || `local-${Date.now()}`,
                    createdAt: new Date().toISOString(),
                    source: 'manual',
                    sourceDisplay: 'Added Component'
                }];
                localStorage.setItem('addedComponents', JSON.stringify(updatedComponents));

                window.dispatchEvent(new CustomEvent('componentsUpdated', {
                    detail: {
                        addedComponents: updatedComponents,
                        type: 'added'
                    }
                }));

                // Set sort to oldest to ensure new component appears at bottom
                setSortBy("oldest");

                // Fetch components to refresh the list (sorted oldest first, so new component appears at bottom)
                await fetchComponents();
                await fetchAddedComponents();

                setShowAddComponentModal(false);

                // Scroll to bottom to show the newly added component after a short delay
                setTimeout(() => {
                    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                }, 300);
                setNewComponent({
                    name: "",
                    cost: "",
                    costUnit: "INR",
                    gravity: "",
                    minVolume: "",
                    maxVolume: "",
                    availableVolume: "",
                    volumeUnit: "L",
                    density: "",
                    selectedProperties: [],
                    selectedGroups: [],
                });
                setPropertyType("global");
                setShouldRefresh(true);
            }
        } catch (error) {
            console.error("Error adding component:", error);
            addNotification("error", `Failed to add component: ${error.message}`);
        }
    }

    const handleBulkDeleteSelection = (componentId) => {
        setSelectedForBulkDelete(prev => {
            if (prev.includes(componentId)) {
                return prev.filter(id => id !== componentId);
            } else {
                return [...prev, componentId];
            }
        });
    };

    const handleSelectAllForBulkDelete = () => {
        const allComponents = getUniqueComponents();
        if (selectedForBulkDelete.length === allComponents.length) {
            setSelectedForBulkDelete([]);
        } else {
            setSelectedForBulkDelete(allComponents.map(comp => comp._id || comp.id));
        }
    };

    const handleBulkDelete = () => {
        if (selectedForBulkDelete.length === 0) {
            addNotification("error", "Please select at least one component to delete");
            return;
        }
        setShowBulkDeleteConfirm(true);
    };

    const handleBulkDeleteConfirm = async () => {
        if (selectedForBulkDelete.length === 0) return;

        try {
            const allComponents = getUniqueComponents();
            const componentsToDelete = allComponents.filter(comp =>
                selectedForBulkDelete.includes(comp._id || comp.id)
            );

            let successCount = 0;
            let errorCount = 0;

            for (const component of componentsToDelete) {
                try {
                    let result;
                    const isAddedComponent = component.source === "manual";

                    if (isAddedComponent) {
                        result = await deleteAddedComponent(component._id);
                    } else {
                        result = await deleteComponentFromMongoDB(component._id);
                    }

                    if (result.success) {
                        successCount++;
                    } else {
                        errorCount++;
                    }
                } catch (error) {
                    console.error(`Error deleting component ${component.name}:`, error);
                    errorCount++;
                }
            }

            if (successCount > 0) {
                addNotification("success", `Successfully deleted ${successCount} component(s)`);
                await fetchComponents();
                await fetchAddedComponents();
                setShouldRefresh(true);
            }

            if (errorCount > 0) {
                addNotification("error", `Failed to delete ${errorCount} component(s)`);
            }

            setSelectedForBulkDelete([]);
        } catch (error) {
            console.error("Error in bulk delete:", error);
            addNotification("error", "Failed to delete components");
        }

        setShowBulkDeleteConfirm(false);
    };

    const handleDeleteComponent = (component, isAddedComponent = false) => {
        setComponentToDelete({ ...component, isAddedComponent })
        setShowDeleteConfirm(true)
    }

    const handleDeleteConfirm = async () => {
        if (!componentToDelete) return

        try {
            let result;

            if (componentToDelete.isAddedComponent) {
                result = await deleteAddedComponent(componentToDelete._id);
            } else {
                result = await deleteComponentFromMongoDB(componentToDelete._id);
            }

            if (result.success) {
                addNotification("success", `Component "${componentToDelete.name}" deleted successfully`);
                await fetchComponents();
                await fetchAddedComponents();
            }

            setShouldRefresh(true);
        } catch (error) {
            console.error("Error deleting component:", error);
            addNotification("error", "Failed to delete component");
        }

        setShowDeleteConfirm(false);
        setComponentToDelete(null);
    }

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false)
        setComponentToDelete(null)
    }

    const handleEditComponent = (component, isAddedComponent = false) => {
        setEditingComponent({ ...component, isAddedComponent });

        loadPropertyAndGroupOptions();

        const selectedProperties = component.selectedProperties || [];
        const selectedGroups = component.selectedGroups || [];

        setEditedComponentData({
            ...component,
            cost: component.cost !== undefined && component.cost !== null ? component.cost : "",
            gravity: component.gravity !== undefined && component.gravity !== null ? component.gravity : "",
            minVolume: component.minVolume || "",
            maxVolume: component.maxVolume || "",
            availableVolume: component.availableVolume || "",
            density: component.density || "",
            selectedProperties: selectedProperties.map(prop => ({
                ...prop,
                editableDefaultValue: prop.defaultValue
            })),
            selectedGroups: selectedGroups.map(group => ({
                ...group,
                editableDefaultValue: group.defaultValue
            })),
        })
        setShowEditComponentPopup(true)
    }

    const handleEditComponentSave = async () => {
        if (!editingComponent) return

        try {
            const allSelectedItems = [...(editedComponentData.selectedProperties || []), ...(editedComponentData.selectedGroups || [])];
            const selectedPropertyText = allSelectedItems.map(item =>
                `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || item.defaultValue}`
            ).join("; ");

            const gravityValue = editedComponentData.gravity !== "" ? Number(editedComponentData.gravity) : 0;

            const updateData = {
                name: editedComponentData.name,
                cost: editedComponentData.cost ? Number(editedComponentData.cost) : 0,
                costUnit: editedComponentData.costUnit,
                gravity: gravityValue,
                selectedProperty: selectedPropertyText,
                selectedProperties: (editedComponentData.selectedProperties || []).map(prop => ({
                    ...prop,
                    defaultValue: prop.editableDefaultValue || prop.defaultValue
                })),
                selectedGroups: (editedComponentData.selectedGroups || []).map(group => ({
                    ...group,
                    defaultValue: group.editableDefaultValue || group.defaultValue
                })),
                minVolume: editedComponentData.minVolume ? Number(editedComponentData.minVolume) : 0,
                maxVolume: editedComponentData.maxVolume ? Number(editedComponentData.maxVolume) : 1000,
                availableVolume: editedComponentData.availableVolume ? Number(editedComponentData.availableVolume) : 500,
                volumeUnit: editedComponentData.volumeUnit,
                density: editedComponentData.density ? Number(editedComponentData.density) : 0,
            };

            console.log("Updating component with gravity:", gravityValue);

            let result;

            if (editingComponent.isAddedComponent) {
                result = await updateAddedComponent(editingComponent._id, updateData);
            } else {
                result = await updateComponentInMongoDB(editingComponent._id, updateData);
            }

            if (result.success) {
                addNotification("success", `Component "${updateData.name}" updated successfully`);
                await fetchComponents();
                await fetchAddedComponents();
            }

            setShowEditComponentPopup(false);
            setEditingComponent(null);
            setEditedComponentData({});
            setShouldRefresh(true);
        } catch (error) {
            console.error("Error updating component:", error);
            addNotification("error", "Failed to update component");
        }
    }

    const handleEditComponentCancel = () => {
        setShowEditComponentPopup(false)
        setEditingComponent(null)
        setEditedComponentData({})
    }

    const handleComponentFieldChange = (field, value) => {
        setEditedComponentData((prev) => ({
            ...prev,
            [field]: value
        }))
    }

    const handleEditPropertySelection = (item) => {
        // if (propertyType === "global") {
        //     setEditedComponentData((prev) => {
        //         let currentProperties = prev.selectedProperties || [];

        //         const isSelected = currentProperties.some(p => p.name === item.name)

        //         if (isSelected) {
        //             currentProperties = currentProperties.filter(p => p.name !== item.name)
        //         } else {
        //             currentProperties = [...currentProperties, {
        //                 ...item,
        //                 editableDefaultValue: item.defaultValue
        //             }]
        //         }

        //         const allSelectedItems = [...currentProperties, ...(prev.selectedGroups || [])];
        //         const selectedPropertyText = allSelectedItems.map(item =>
        //             `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || item.defaultValue}`
        //         ).join("; ");

        //         return {
        //             ...prev,
        //             selectedProperties: currentProperties,
        //             selectedProperty: selectedPropertyText
        //         }
        //     })

        if (propertyType === "global") {
            setEditedComponentData((prev) => {
                let currentProperties = prev.selectedProperties || [];

                const isSelected = currentProperties.some(p => p.name === item.name)

                if (isSelected) {
                    currentProperties = currentProperties.filter(p => p.name !== item.name)
                } else {
                    currentProperties = [...currentProperties, {
                        ...item,
                        editableDefaultValue: 0 // Force 0 instead of item.defaultValue
                    }]
                }

                const allSelectedItems = [...currentProperties, ...(prev.selectedGroups || [])];
                const selectedPropertyText = allSelectedItems.map(item =>
                    `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || 0}`
                ).join("; ");

                return {
                    ...prev,
                    selectedProperties: currentProperties,
                    selectedProperty: selectedPropertyText
                }
            })
        } else {
            setEditedComponentData((prev) => {
                let currentGroups = prev.selectedGroups || [];

                const isSelected = currentGroups.some(g => g.name === item.name)

                if (isSelected) {
                    currentGroups = currentGroups.filter(g => g.name !== item.name)
                } else {
                    currentGroups = [...currentGroups, {
                        ...item,
                        editableDefaultValue: item.defaultValue
                    }]
                }

                const allSelectedItems = [...(prev.selectedProperties || []), ...currentGroups];
                const selectedPropertyText = allSelectedItems.map(item =>
                    `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || item.defaultValue}`
                ).join("; ");

                return {
                    ...prev,
                    selectedGroups: currentGroups,
                    selectedProperty: selectedPropertyText
                }
            })
        }
    }

    const handleEditDefaultValueChange = (itemName, newValue, isGlobal = true) => {
        if (isGlobal) {
            setEditedComponentData(prev => ({
                ...prev,
                selectedProperties: (prev.selectedProperties || []).map(prop =>
                    prop.name === itemName
                        ? { ...prop, editableDefaultValue: newValue }
                        : prop
                )
            }));
        } else {
            setEditedComponentData(prev => ({
                ...prev,
                selectedGroups: (prev.selectedGroups || []).map(group =>
                    group.name === itemName
                        ? { ...group, editableDefaultValue: newValue }
                        : group
                )
            }));
        }
    }

    const handleEditSelectAllProperties = () => {
        if (propertyType === "global") {
            setEditedComponentData((prev) => {
                const currentProperties = prev.selectedProperties || [];

                if (currentProperties.length === globalPropertyOptions.length) {
                    const allSelectedItems = [...(prev.selectedGroups || [])];
                    const selectedPropertyText = allSelectedItems.map(item =>
                        `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || item.defaultValue}`
                    ).join("; ");

                    return {
                        ...prev,
                        selectedProperties: [],
                        selectedProperty: selectedPropertyText
                    }
                } else {
                    const updatedProperties = globalPropertyOptions.map(prop => ({
                        ...prop,
                        editableDefaultValue: prop.defaultValue
                    }));
                    const allSelectedItems = [...updatedProperties, ...(prev.selectedGroups || [])];
                    const selectedPropertyText = allSelectedItems.map(item =>
                        `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || item.defaultValue}`
                    ).join("; ");

                    return {
                        ...prev,
                        selectedProperties: updatedProperties,
                        selectedProperty: selectedPropertyText
                    }
                }
            })
        } else {
            setEditedComponentData((prev) => {
                const currentGroups = prev.selectedGroups || [];

                if (currentGroups.length === groupPropertyOptions.length) {
                    const allSelectedItems = [...(prev.selectedProperties || [])];
                    const selectedPropertyText = allSelectedItems.map(item =>
                        `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || item.defaultValue}`
                    ).join("; ");

                    return {
                        ...prev,
                        selectedGroups: [],
                        selectedProperty: selectedPropertyText
                    }
                } else {
                    const updatedGroups = groupPropertyOptions.map(group => ({
                        ...group,
                        editableDefaultValue: group.defaultValue
                    }));
                    const allSelectedItems = [...(prev.selectedProperties || []), ...updatedGroups];
                    const selectedPropertyText = allSelectedItems.map(item =>
                        `${item.name} (${item.uom}) - Min: ${item.minValue}, Max: ${item.maxValue}, Default: ${item.editableDefaultValue || item.defaultValue}`
                    ).join("; ");

                    return {
                        ...prev,
                        selectedGroups: updatedGroups,
                        selectedProperty: selectedPropertyText
                    }
                }
            })
        }
    }

    const handleEditGroup = (group) => {
        setEditingGroup(group);
        setEditingGroupName(group.name); // Set current group name for editing
        setGroupComponentsToAdd([]);
        setShowEditGroupPopup(true);
    }

    // Function to update group name
    const handleUpdateGroupName = async () => {
        if (!editingGroup || !editingGroupName.trim()) {
            addNotification("error", "Please enter a valid group name");
            return;
        }

        try {
            const updateData = {
                ...editingGroup,
                name: editingGroupName.trim()
            };

            const result = await updateGroupReport(editingGroup._id, updateData);

            if (result.success) {
                addNotification("success", `Group name updated to "${editingGroupName.trim()}"`);
                await fetchGroupReports();
                setEditingGroup(updateData);
            }
        } catch (error) {
            console.error("Error updating group name:", error);
            addNotification("error", "Failed to update group name");
        }
    }

    const handleAddComponentsToGroup = async () => {
        if (!editingGroup || groupComponentsToAdd.length === 0) {
            addNotification("error", "Please select components to add");
            return;
        }

        try {
            const selectedComponentsData = groupComponentsToAdd
                .map((key) => {
                    const [type, index] = key.split("-")
                    const numericIndex = Number(index)
                    let component = null

                    if (type === "api") {
                        if (numericIndex >= 0 && numericIndex < apiComponents.length) {
                            component = apiComponents[numericIndex]
                        }
                    } else if (type === "added") {
                        if (numericIndex >= 0 && numericIndex < addedComponents.length) {
                            component = addedComponents[numericIndex]
                        }
                    }

                    if (!component) return null

                    const selectedPropertyNames = getSelectedPropertyNames(component);
                    const selectedPropertyText = selectedPropertyNames.join(", ");

                    return {
                        name: component.name,
                        volume: component.availableVolume || 1000,
                        cost: component.cost || 0,
                        gravity: component.gravity || 0,
                        costUnit: component.costUnit || currency,
                        properties: component.otherProductList || [],
                        selectedProperty: selectedPropertyText || "",
                        selectedProperties: component.selectedProperties || [],
                        selectedGroups: component.selectedGroups || [],
                        source: component.source || type
                    }
                })
                .filter(Boolean)

            if (selectedComponentsData.length === 0) {
                addNotification("error", "No valid components selected")
                return
            }

            const existingVolume = editingGroup.totalVolume || 0;
            const existingCost = editingGroup.totalCost || 0;

            const newComponentsVolume = selectedComponentsData.reduce((sum, comp) => sum + (comp.volume || 1000), 0);
            const newComponentsCost = selectedComponentsData.reduce((sum, comp) => sum + ((comp.cost || 0) * (comp.volume || 1000)), 0);

            const updatedComponents = [
                ...editingGroup.components,
                ...selectedComponentsData.map(comp => ({
                    ...comp,
                    percentage: ((comp.volume || 1000) / (existingVolume + newComponentsVolume)) * 100
                }))
            ];

            const totalVolume = existingVolume + newComponentsVolume;
            const updatedComponentsWithPercentages = updatedComponents.map(comp => ({
                ...comp,
                percentage: ((comp.volume || 1000) / totalVolume) * 100
            }));

            const updateData = {
                ...editingGroup,
                components: updatedComponentsWithPercentages,
                totalCost: existingCost + newComponentsCost,
                totalVolume: totalVolume
            };

            const result = await updateGroupReport(editingGroup._id, updateData);

            if (result.success) {
                addNotification("success", `Added ${selectedComponentsData.length} components to group "${editingGroup.name}"`);
                await fetchGroupReports();
                setEditingGroup(updateData);
                setGroupComponentsToAdd([]);
            }
        } catch (error) {
            console.error("Error adding components to group:", error);
            addNotification("error", `Failed to add components to group: ${error.message}`);
        }
    }

    const handleRemoveComponentFromGroup = async (componentIndex) => {
        if (!editingGroup) return;

        try {
            const updatedComponents = [...editingGroup.components];
            updatedComponents.splice(componentIndex, 1);

            const totalVolume = updatedComponents.reduce((sum, comp) => sum + (comp.volume || 1000), 0);
            const totalCost = updatedComponents.reduce((sum, comp) => sum + ((comp.cost || 0) * (comp.volume || 1000)), 0);

            const updatedComponentsWithPercentages = updatedComponents.map(comp => ({
                ...comp,
                percentage: ((comp.volume || 1000) / totalVolume) * 100
            }));

            const updateData = {
                ...editingGroup,
                components: updatedComponentsWithPercentages,
                totalCost: totalCost,
                totalVolume: totalVolume
            };

            const result = await updateGroupReport(editingGroup._id, updateData);

            if (result.success) {
                addNotification("success", "Component removed from group");
                await fetchGroupReports();
                setEditingGroup(updateData);
            }
        } catch (error) {
            console.error("Error removing component from group:", error);
            addNotification("error", "Failed to remove component from group");
        }
    }

    const handleCreateGroup = async () => {
        if (!groupName.trim()) {
            addNotification("error", "Please enter a group name")
            return
        }

        if (selectedComponents.length === 0) {
            addNotification("error", "Please select at least one component")
            return
        }

        const selectedComponentsData = selectedComponents
            .map((key) => {
                const [type, index] = key.split("-")
                const numericIndex = Number(index)
                let component = null

                if (type === "api") {
                    if (numericIndex >= 0 && numericIndex < apiComponents.length) {
                        component = apiComponents[numericIndex]
                    }
                } else if (type === "added") {
                    if (numericIndex >= 0 && numericIndex < addedComponents.length) {
                        component = addedComponents[numericIndex]
                    }
                }

                if (!component) return null

                const selectedPropertyNames = getSelectedPropertyNames(component);
                const selectedPropertyText = selectedPropertyNames.join(", ");

                return {
                    name: component.name,
                    volume: component.availableVolume || 1000,
                    cost: component.cost || 0,
                    gravity: component.gravity || 0,
                    costUnit: component.costUnit || currency,
                    properties: component.otherProductList || [],
                    selectedProperty: selectedPropertyText || "",
                    selectedProperties: component.selectedProperties || [],
                    selectedGroups: component.selectedGroups || [],
                    source: component.source || type
                }
            })
            .filter(Boolean)

        if (selectedComponentsData.length === 0) {
            addNotification("error", "No valid components selected")
            return
        }

        const totalVolume = selectedComponentsData.reduce((sum, comp) => sum + (comp.volume || 1000), 0);
        const totalCost = selectedComponentsData.reduce((sum, comp) => sum + ((comp.cost || 0) * (comp.volume || 1000)), 0);

        const groupReportData = {
            name: groupName.trim(),
            description: groupDescription.trim() || `Group report for ${groupName.trim()}`,
            components: selectedComponentsData.map(comp => ({
                ...comp,
                percentage: ((comp.volume || 1000) / totalVolume) * 100
            })),
            totalCost,
            totalVolume,
            currency,
            volumeUnit: "L",
            createdBy: currentUser?.username || 'unknown'
        };

        try {
            const result = await createGroupReportFromGroup(groupReportData);

            if (result.success) {
                addNotification("success", `Group "${groupName}" created successfully`);

                const existingGroups = JSON.parse(localStorage.getItem('componentGroups') || '[]');
                const updatedGroups = [...existingGroups, {
                    ...groupReportData,
                    _id: result.data?._id || `local-${Date.now()}`,
                    id: result.data?._id || `local-${Date.now()}`,
                    createdAt: new Date().toISOString()
                }];
                localStorage.setItem('componentGroups', JSON.stringify(updatedGroups));

                window.dispatchEvent(new CustomEvent('componentGroupsUpdated', {
                    detail: { groups: updatedGroups }
                }));

                await fetchGroupReports();

                setShowAddGroupPopup(false);
                setSelectedComponents([]);
                setGroupName("");
                setGroupDescription("");

                setShowGroupReport(true);
                setShowGlobalReport(false);
            }
        } catch (error) {
            console.error("Error creating group:", error);
            addNotification("error", `Failed to create group: ${error.message}`);
        }
    }

    const handleDeleteGroupReport = async (reportId) => {
        if (window.confirm("Are you sure you want to delete this group report?")) {
            try {
                const result = await deleteGroupReport(reportId);

                if (result.success) {
                    const existingGroups = JSON.parse(localStorage.getItem('componentGroups') || '[]');
                    const updatedGroups = existingGroups.filter(group => group._id !== reportId && group.id !== reportId);
                    localStorage.setItem('componentGroups', JSON.stringify(updatedGroups));

                    window.dispatchEvent(new CustomEvent('componentGroupsUpdated', {
                        detail: { groups: updatedGroups }
                    }));

                    addNotification("success", "Group report deleted successfully");
                    await fetchGroupReports();
                }
            } catch (error) {
                console.error("Error deleting group report:", error);
                addNotification("error", "Failed to delete group report");
            }
        }
    };

    const toggleGroupReportExpansion = (reportId) => {
        setExpandedGroupReport(expandedGroupReport === reportId ? null : reportId)
    }

    // const getUniqueComponents = () => {
    //     const allComponents = [];
    //     const seen = new Set();

    //     // Add API components first (oldest first)
    //     apiComponents.forEach((component, index) => {
    //         const key = `${component.name}-api`;
    //         if (!seen.has(key)) {
    //             seen.add(key);
    //             allComponents.push({
    //                 ...component,
    //                 source: component.source || 'api',
    //                 sourceDisplay: component.source === 'excel-imported' ? 'Excel Imported' : 'API Component',
    //                 isAddedComponent: false
    //             });
    //         }
    //     });

    //     // Add added components at the end (newest at the bottom)
    //     addedComponents.forEach((component, index) => {
    //         const key = `${component.name}-added`;
    //         if (!seen.has(key)) {
    //             seen.add(key);
    //             allComponents.push({
    //                 ...component,
    //                 source: 'manual',
    //                 sourceDisplay: 'Added Component',
    //                 isAddedComponent: true
    //             });
    //         }
    //     });

    //     return allComponents;
    // };



    const getUniqueComponents = () => {
        const allComponents = [];
        const seen = new Set();

        // Add API components (oldest first - already sorted)
        apiComponents.forEach((component, index) => {
            const key = `${component.name}-api`;
            if (!seen.has(key)) {
                seen.add(key);
                allComponents.push({
                    ...component,
                    source: component.source || 'api',
                    sourceDisplay: component.source === 'excel-imported' ? 'Excel Imported' : 'API Component',
                    isAddedComponent: false
                });
            }
        });

        // Add added components (oldest first - already sorted)
        addedComponents.forEach((component, index) => {
            const key = `${component.name}-added`;
            if (!seen.has(key)) {
                seen.add(key);
                allComponents.push({
                    ...component,
                    source: 'manual',
                    sourceDisplay: 'Added Component',
                    isAddedComponent: true
                });
            }
        });

        return allComponents;
    };


    const downloadGlobalReportCSV = () => {
        const allComponents = getUniqueComponents();

        const headers = ["Component Name", "Cost", "Cost Unit", "Gravity", "Source", "Selected Properties", "Date Added"];

        const rows = allComponents.map((comp) => {
            const baseRow = [
                comp.name || "",
                comp.cost || "",
                comp.costUnit || currency,
                comp.gravity || "",
                comp.sourceDisplay || 'API Component',
                getSelectedPropertyNames(comp).join(", ") || "-",
                new Date(comp.timestamp || comp.createdAt || Date.now()).toLocaleDateString(),
            ];

            return baseRow;
        });

        const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "global_components_report.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        addNotification("success", "Global components report exported successfully");
    };

    const downloadGroupReportCSV = (report) => {
        const headers = ["Component Name", "Cost", "Gravity", "Selected Properties", "Volume", "Volume Unit", "Percentage", "Total Cost"]

        const rows = report.components.map((comp) => [
            comp.name || "",
            comp.cost || "",
            comp.gravity || "",
            comp.selectedProperty || "",
            comp.volume || "",
            report.volumeUnit || "",
            `${comp.percentage?.toFixed(2)}%` || "",
            `${report.currency}${((comp.cost || 0) * (comp.volume || 0)).toFixed(2)}`
        ])

        const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n")

        const blob = new Blob([csvContent], { type: "text/csv" })
        const url = URL.revokeObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = `${report.name}_group_report.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    const handleAddGroupClick = () => {
        setShowAddGroupPopup(true)
        setSelectedComponents([])
        setGroupName("")
        setGroupDescription("")
    }

    const handleComponentSelection = (componentKey) => {
        setSelectedComponents((prev) => {
            const isSelected = prev.includes(componentKey)
            if (isSelected) {
                return prev.filter((key) => key !== componentKey)
            } else {
                return [...prev, componentKey]
            }
        })
    }

    const handleGroupComponentSelection = (componentKey) => {
        setGroupComponentsToAdd((prev) => {
            const isSelected = prev.includes(componentKey)
            if (isSelected) {
                return prev.filter((key) => key !== componentKey)
            } else {
                return [...prev, componentKey]
            }
        })
    }

    const handleSelectAllComponents = () => {
        const allComponents = [...apiComponents, ...addedComponents]
        const allComponentKeys = allComponents.map((_, index) => {
            if (index < apiComponents.length) {
                return `api-${index}`
            } else {
                return `added-${index - apiComponents.length}`
            }
        })

        if (selectedComponents.length === allComponentKeys.length) {
            setSelectedComponents([])
        } else {
            setSelectedComponents(allComponentKeys)
        }
    }

    const handleSelectAllGroupComponents = () => {
        const allComponents = [...apiComponents, ...addedComponents]
        const allComponentKeys = allComponents.map((_, index) => {
            if (index < apiComponents.length) {
                return `api-${index}`
            } else {
                return `added-${index - apiComponents.length}`
            }
        })

        if (groupComponentsToAdd.length === allComponentKeys.length) {
            setGroupComponentsToAdd([])
        } else {
            setGroupComponentsToAdd(allComponentKeys)
        }
    }

    const handlePreviewComponentSelection = (componentId) => {
        setSelectedPreviewComponents(prev => {
            if (prev.includes(componentId)) {
                return prev.filter(id => id !== componentId);
            } else {
                return [...prev, componentId];
            }
        });
    };

    const handleSelectAllPreviewComponents = () => {
        if (selectedPreviewComponents.length === excelPreviewData.length) {
            setSelectedPreviewComponents([]);
        } else {
            setSelectedPreviewComponents(excelPreviewData.map(comp => comp.id));
        }
    };

    const applyFilters = (components) => {
        return components.filter((component) => {
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase()
                const matchesSearch =
                    component.name.toLowerCase().includes(searchLower) ||
                    (component.sourceSet && component.sourceSet.toLowerCase().includes(searchLower)) ||
                    (component.selectedProperty && component.selectedProperty.toLowerCase().includes(searchLower))
                if (!matchesSearch) return false
            }

            const cost = Number(component.cost) || 0
            const gravity = Number(component.gravity) || 0

            if (filters.minCost && cost < Number(filters.minCost)) return false
            if (filters.maxCost && cost > Number(filters.maxCost)) return false
            if (filters.minGravity && gravity < Number(filters.minGravity)) return false
            if (filters.maxGravity && gravity > Number(filters.maxGravity)) return false

            return true
        })
    }

    const uniqueComponents = getUniqueComponents()
    const filteredUniqueComponents = applyFilters(uniqueComponents)

    filteredUniqueComponents.sort((a, b) => {
        if (sortBy === "newest") return new Date(b.createdAt || b.timestamp) - new Date(a.createdAt || a.timestamp)
        if (sortBy === "oldest") return new Date(a.createdAt || a.timestamp) - new Date(b.createdAt || b.timestamp)
        if (sortBy === "name") return a.name.localeCompare(b.name)
        return 0
    })

    const totalPages = Math.ceil(filteredUniqueComponents.length / itemsPerPage)
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const currentGlobalItems = filteredUniqueComponents.slice(startIndex, endIndex)

    const handlePageChange = (page) => {
        setCurrentPage(page)
        window.scrollTo({ top: 0, behavior: "smooth" })
    }

    const getPageNumbers = () => {
        const pageNumbers = []
        const maxVisiblePages = 5

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i)
            }
        } else {
            const startPage = Math.max(1, currentPage - 2)
            const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

            for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i)
            }
        }

        return pageNumbers
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
            <NotificationToaster notifications={notifications} />

            <LogoutConfirmModal
                isOpen={showLogoutConfirm}
                onConfirm={handleConfirmLogout}
                onCancel={handleCancelLogout}
            />

            {/* Bulk Delete Confirmation Modal */}
            {showBulkDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-sm w-full transform transition-all">
                        <div className="p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                    <svg className="w-4 h-4 md:w-5 md:h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-gray-900">Delete Selected Components</h3>
                                    <p className="text-gray-600 text-sm mt-0.5">
                                        Are you sure you want to delete {selectedForBulkDelete.length} selected component(s)? This action cannot be undone.
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-2 mt-4">
                                <button
                                    onClick={() => setShowBulkDeleteConfirm(false)}
                                    className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-200 text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleBulkDeleteConfirm}
                                    className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors duration-200 flex items-center justify-center text-sm"
                                >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                    </svg>
                                    Delete ({selectedForBulkDelete.length})
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showEditGroupPopup && editingGroup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-4 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl transform transition-all">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-bold text-gray-900">Edit Group: {editingGroup.name}</h3>
                            </div>
                            <button
                                onClick={() => {
                                    setShowEditGroupPopup(false);
                                    setEditingGroup(null);
                                    setGroupComponentsToAdd([]);
                                    setEditingGroupName("");
                                }}
                                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Group Name Update Section */}
                        <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <h4 className="text-sm font-semibold text-blue-900 mb-2">Update Group Name</h4>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={editingGroupName}
                                    onChange={(e) => setEditingGroupName(e.target.value)}
                                    placeholder="Enter new group name"
                                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                                />
                                <button
                                    onClick={handleUpdateGroupName}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-1 text-sm"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Update Name
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 flex-1 overflow-hidden">
                            <div className="bg-gray-50 rounded-lg p-3 md:p-4 border border-gray-200 overflow-hidden flex flex-col">
                                <h4 className="text-sm md:text-base font-semibold text-gray-900 mb-3">Current Group Components ({editingGroup.components.length})</h4>
                                <div className="flex-1 overflow-y-auto">
                                    <div className="space-y-2">
                                        {editingGroup.components.map((component, index) => (
                                            <div key={index} className="bg-white rounded-lg border border-gray-200 p-2 md:p-3 flex justify-between items-center">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex justify-between items-start">
                                                        <span className="font-medium text-gray-900 text-xs md:text-sm truncate">{component.name}</span>
                                                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded ml-2 flex-shrink-0">
                                                            {component.percentage?.toFixed(1)}%
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-gray-600 mt-1">
                                                        Cost: {editingGroup.currency}{component.cost.toFixed(2)} | Gravity: {component.gravity} | Volume: {component.volume} {editingGroup.volumeUnit}
                                                    </div>
                                                    {component.selectedProperty && (
                                                        <div className="text-xs text-gray-500 mt-1 truncate">
                                                            Properties: {component.selectedProperty}
                                                        </div>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={() => handleRemoveComponentFromGroup(index)}
                                                    className="ml-2 md:ml-3 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                                                    title="Remove from group"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="flex justify-between text-xs md:text-sm">
                                        <span className="font-medium">Total Components:</span>
                                        <span>{editingGroup.components.length}</span>
                                    </div>
                                    <div className="flex justify-between text-xs md:text-sm">
                                        <span className="font-medium">Total Volume:</span>
                                        <span>{editingGroup.totalVolume} {editingGroup.volumeUnit}</span>
                                    </div>
                                    <div className="flex justify-between text-xs md:text-sm">
                                        <span className="font-medium">Total Cost:</span>
                                        <span>{editingGroup.currency}{editingGroup.totalCost.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Add Components to Group */}
                            <div className="bg-gray-50 rounded-lg p-3 md:p-4 border border-gray-200 overflow-hidden flex flex-col">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm md:text-base font-semibold text-gray-900">
                                        Add Components ({groupComponentsToAdd.length} selected)
                                    </h4>
                                    <button
                                        onClick={handleSelectAllGroupComponents}
                                        className="px-2 md:px-3 py-1 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors text-xs"
                                    >
                                        {groupComponentsToAdd.length === (apiComponents.length + addedComponents.length) ? "Deselect All" : "Select All"}
                                    </button>
                                </div>

                                <div className="flex-1 overflow-y-auto">
                                    <div className="space-y-2">
                                        {/* API Components */}
                                        {apiComponents.map((component, index) => {
                                            const componentKey = `api-${index}`
                                            const isSelected = groupComponentsToAdd.includes(componentKey)

                                            return (
                                                <div
                                                    key={generateUniqueKey(component, index, 'api')}
                                                    className={`bg-white rounded-lg border p-2 md:p-3 cursor-pointer transition-colors ${isSelected ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:bg-gray-50"}`}
                                                    onClick={() => handleGroupComponentSelection(componentKey)}
                                                >
                                                    <div className="flex items-center gap-2 md:gap-3">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => handleGroupComponentSelection(componentKey)}
                                                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0"
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex justify-between items-start">
                                                                <span className="font-medium text-gray-900 text-xs md:text-sm truncate">{component.name}</span>
                                                                <span className="text-xs text-gray-500 bg-blue-100 px-2 py-1 rounded ml-2 flex-shrink-0">
                                                                    API
                                                                </span>
                                                            </div>
                                                            <div className="text-xs text-gray-600 mt-1">
                                                                Cost: {getCurrencySymbol(component.costUnit)}{component.cost.toFixed(2)} | Gravity: {component.gravity}
                                                            </div>
                                                            {component.selectedProperties && component.selectedProperties.length > 0 && (
                                                                <div className="text-xs text-gray-500 mt-1 truncate">
                                                                    Properties: {component.selectedProperties.map(p => p.name).join(", ")}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        })}

                                        {/* Added Components */}
                                        {addedComponents.map((component, index) => {
                                            const componentKey = `added-${index}`
                                            const isSelected = groupComponentsToAdd.includes(componentKey)

                                            return (
                                                <div
                                                    key={generateUniqueKey(component, index, 'added')}
                                                    className={`bg-white rounded-lg border p-2 md:p-3 cursor-pointer transition-colors ${isSelected ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:bg-gray-50"}`}
                                                    onClick={() => handleGroupComponentSelection(componentKey)}
                                                >
                                                    <div className="flex items-center gap-2 md:gap-3">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => handleGroupComponentSelection(componentKey)}
                                                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 flex-shrink-0"
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex justify-between items-start">
                                                                <span className="font-medium text-gray-900 text-xs md:text-sm truncate">{component.name}</span>
                                                                <span className="text-xs text-gray-500 bg-green-100 px-2 py-1 rounded ml-2 flex-shrink-0">
                                                                    Added
                                                                </span>
                                                            </div>
                                                            <div className="text-xs text-gray-600 mt-1">
                                                                Cost: {getCurrencySymbol(component.costUnit)}{component.cost.toFixed(2)} | Gravity: {component.gravity}
                                                            </div>
                                                            {component.selectedProperties && component.selectedProperties.length > 0 && (
                                                                <div className="text-xs text-gray-500 mt-1 truncate">
                                                                    Properties: {component.selectedProperties.map(p => p.name).join(", ")}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>

                                <div className="mt-3 pt-3 border-t border-gray-200">
                                    <button
                                        onClick={handleAddComponentsToGroup}
                                        disabled={groupComponentsToAdd.length === 0}
                                        className={`property-btn flex items-center gap-2 justify-center w-full ${groupComponentsToAdd.length === 0
                                            ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                                            : "bg-blue-600 text-white hover:bg-blue-700"
                                            }`}
                                    >
                                        <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        Add Selected ({groupComponentsToAdd.length})
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Excel Import Popup */}
            {showExcelImportPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 md:p-4">
                    <div className="bg-white rounded-xl p-3 md:p-4 max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 md:w-8 md:h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 md:w-4 md:h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                                        />
                                    </svg>
                                </div>
                                <h3 className="text-base md:text-lg font-bold text-gray-900">Import Components from Excel</h3>
                            </div>
                            <button
                                onClick={() => {
                                    setShowExcelImportPopup(false);
                                    setShowExcelPreview(false);
                                    setExcelPreviewData([]);
                                    setSelectedPreviewComponents([]);
                                    setFile(null);
                                }}
                                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 md:p-4 text-center hover:border-blue-400 transition-all duration-300 bg-gray-50 hover:bg-blue-50">
                                <div className="max-w-md mx-auto">
                                    <svg className="w-5 h-5 md:w-6 md:h-6 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                    <div className="flex text-sm text-gray-600 justify-center">
                                        <label htmlFor="excel-file-components-popup" className="relative cursor-pointer rounded-lg font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none bg-white px-3 py-2 border border-blue-200 hover:border-blue-300 transition-colors">
                                            <span>Upload Excel file</span>
                                            <input
                                                id="excel-file-components-popup"
                                                name="excel-file-components-popup"
                                                type="file"
                                                accept=".xlsx, .xls"
                                                className="sr-only"
                                                onChange={onChooseFile}
                                            />
                                        </label>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">.XLSX or .XLS files only, up to 10MB</p>
                                    {file && (
                                        <div className="mt-2 p-2 bg-white rounded-lg border border-green-200">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center">
                                                    <svg className="w-3 h-3 text-green-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                    <span className="text-xs font-medium text-gray-700 truncate max-w-[120px]">{file.name}</span>
                                                </div>
                                                <span className="text-xs text-gray-500 bg-gray-100 px-1 py-0.5 rounded">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {file && !showExcelPreview && (
                                <div className="flex justify-center">
                                    <button
                                        onClick={loadExcelForPreview}
                                        disabled={loading}
                                        className={`px-3 md:px-4 py-2 rounded-lg font-medium component-btn flex items-center gap-1 text-xs md:text-sm ${loading
                                            ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                                            : "bg-blue-600 text-white hover:bg-blue-700"
                                            }`}
                                    >
                                        {loading ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <span>Loading...</span>
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                                </svg>
                                                <span>Load Excel Data</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}

                            {showExcelPreview && excelPreviewData.length > 0 && (
                                <div className="bg-gray-50 rounded-lg p-3 md:p-4 border border-gray-200">
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="text-sm md:text-base font-semibold text-gray-900">
                                            Excel Data Preview ({selectedPreviewComponents.length}/{excelPreviewData.length} selected)
                                        </h4>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={handleSelectAllPreviewComponents}
                                                className="px-2 md:px-3 py-1 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors text-xs"
                                            >
                                                {selectedPreviewComponents.length === excelPreviewData.length ? "Deselect All" : "Select All"}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="overflow-x-auto max-h-48 md:max-h-64">
                                        <table className="min-w-full divide-y divide-gray-200 bg-white rounded-lg overflow-hidden text-xs">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="w-8 px-1 md:px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                                        <input
                                                            type="checkbox"
                                                            className="w-3 h-3 text-blue-600 border-gray-300 rounded"
                                                            checked={selectedPreviewComponents.length === excelPreviewData.length}
                                                            onChange={handleSelectAllPreviewComponents}
                                                        />
                                                    </th>
                                                    <th className="px-1 md:px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                                                        Component
                                                    </th>
                                                    <th className="px-1 md:px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase">
                                                        Cost
                                                    </th>
                                                    <th className="px-1 md:px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase">
                                                        Unit
                                                    </th>
                                                    <th className="px-1 md:px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase">
                                                        Specific Gravity
                                                    </th>
                                                    <th className="px-1 md:px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase">
                                                        Min Vol
                                                    </th>
                                                    <th className="px-1 md:px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase">
                                                        Max Vol
                                                    </th>
                                                    <th className="px-1 md:px-2 py-2 text-right text-xs font-semibold text-gray-700 uppercase">
                                                        Available Vol
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {excelPreviewData.map((component) => (
                                                    <tr
                                                        key={component.id}
                                                        className={`hover:bg-gray-50 ${selectedPreviewComponents.includes(component.id) ? 'bg-blue-50' : ''
                                                            }`}
                                                    >
                                                        <td className="px-1 md:px-2 py-2 whitespace-nowrap">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedPreviewComponents.includes(component.id)}
                                                                onChange={() => handlePreviewComponentSelection(component.id)}
                                                                className="w-3 h-3 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                                                            />
                                                        </td>
                                                        <td className="px-1 md:px-2 py-2 text-xs font-medium text-gray-900 truncate max-w-[80px] md:max-w-none">{component.name}</td>
                                                        <td className="px-1 md:px-2 py-2 text-xs text-right text-gray-900">
                                                            {getCurrencySymbol(component.costUnit)}
                                                            {component.cost.toFixed(2)}
                                                        </td>
                                                        <td className="px-1 md:px-2 py-2 text-xs text-gray-600">{component.costUnit}</td>
                                                        <td className="px-1 md:px-2 py-2 text-xs text-right text-gray-600">{component.gravity}</td>
                                                        <td className="px-1 md:px-2 py-2 text-xs text-right text-gray-600">{component.minVolume}</td>
                                                        <td className="px-1 md:px-2 py-2 text-xs text-right text-gray-600">{component.maxVolume}</td>
                                                        <td className="px-1 md:px-2 py-2 text-xs text-right text-gray-600">{component.availableVolume}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="mt-2 text-xs text-gray-600">
                                        <p>
                                            {selectedPreviewComponents.length} out of {excelPreviewData.length} components selected.
                                            Click "Import Selected Components" to add them to your component list.
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-2 justify-center pt-2">
                                <button
                                    onClick={() => {
                                        setShowExcelImportPopup(false);
                                        setShowExcelPreview(false);
                                        setExcelPreviewData([]);
                                        setSelectedPreviewComponents([]);
                                        setFile(null);
                                    }}
                                    className="px-3 md:px-4 py-2 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors text-xs md:text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={importPreviewedComponents}
                                    disabled={loading || selectedPreviewComponents.length === 0}
                                    className={`px-3 md:px-4 py-2 rounded-lg font-medium component-btn flex items-center gap-1 text-xs md:text-sm ${loading || selectedPreviewComponents.length === 0
                                        ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                                        : "bg-green-600 text-white hover:bg-green-700"
                                        }`}
                                >
                                    {loading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span>Importing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                                            </svg>
                                            <span>Import Selected ({selectedPreviewComponents.length})</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Component Modal */}
            {showAddComponentModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 md:p-4">
                    <div className="bg-white rounded-xl p-3 md:p-4 max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 md:w-8 md:h-8 bg-green-100 rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 md:w-4 md:h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                </div>
                                <h3 className="text-base md:text-lg font-bold text-gray-900">Add New Component</h3>
                            </div>
                            <button
                                onClick={() => setShowAddComponentModal(false)}
                                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Component Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={newComponent.name}
                                    onChange={(e) => setNewComponent({ ...newComponent, name: e.target.value })}
                                    placeholder="Enter component name"
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all text-sm"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={newComponent.cost}
                                        onChange={(e) => setNewComponent({ ...newComponent, cost: e.target.value })}
                                        placeholder="0.00"
                                        className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                                    <div className="relative w-full">
                                        <select
                                            value={newComponent.costUnit}
                                            onChange={(e) => setNewComponent({ ...newComponent, costUnit: e.target.value })}
                                            className="w-full appearance-none px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-xl bg-white text-sm md:text-base font-medium shadow-sm focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                                        >
                                            {currencies.map((curr) => (
                                                <option key={curr.code} value={curr.code}>
                                                    {curr.symbol} {curr.code}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="absolute inset-y-0 right-2 md:right-3 flex items-center pointer-events-none">
                                            <svg
                                                className="w-4 h-4 md:w-5 md:h-5 text-gray-500"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Gravity Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Specific Gravity</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={newComponent.gravity}
                                    onChange={(e) => setNewComponent({ ...newComponent, gravity: e.target.value })}
                                    placeholder="0.00"
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all text-sm"
                                />
                            </div>

                            {/* ---------- Global Properties Section ---------- */}
                            {newComponent.selectedProperties.length > 0 && (
                                <div className="border rounded-lg p-2 bg-blue-50 border-blue-200 mb-4">
                                    <h4 className="text-xs font-medium mb-1 text-blue-900">
                                        Selected Global Properties:
                                    </h4>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full text-xs border rounded border-blue-200">
                                            <thead className="bg-blue-100">
                                                <tr>
                                                    <th className="px-1 py-0.5 text-left font-medium border-b text-blue-900">Property</th>
                                                    <th className="px-1 py-0.5 text-left font-medium border-b text-blue-900">UOM</th>
                                                    <th className="px-1 py-0.5 text-left font-medium border-b text-blue-900">Value</th>
                                                    <th className="px-1 py-0.5 text-left font-medium border-b text-blue-900">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {newComponent.selectedProperties.map((item, index) => (
                                                    <tr key={`global-${index}-${item.name}`} className="border-b border-blue-200 last:border-b-0">
                                                        <td className="px-1 py-0.5 text-blue-800 truncate max-w-[80px]">{item.name}</td>
                                                        <td className="px-1 py-0.5 text-blue-700">{item.uom}</td>
                                                        <td className="px-1 py-0.5 text-blue-700">
                                                            <input
                                                                type="number"
                                                                value={item.editableDefaultValue || item.defaultValue}
                                                                onChange={(e) => handleDefaultValueChange(item.name, e.target.value, true)}
                                                                className="w-16 px-1 py-0.5 border border-gray-300 rounded text-xs"
                                                                min={item.minValue}
                                                                max={item.maxValue}
                                                                step="0.1"
                                                            />
                                                        </td>
                                                        <td className="px-1 py-0.5">
                                                            <button
                                                                type="button"
                                                                onClick={() => handlePropertySelection(item)}
                                                                className="text-red-500 hover:text-red-700 text-xs"
                                                            >
                                                                Remove
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* ---------- Group Properties Section ---------- */}
                            {newComponent.selectedGroups.length > 0 && (
                                <div className="border rounded-lg p-2 bg-purple-50 border-purple-200">
                                    <h4 className="text-xs font-medium mb-1 text-purple-900">
                                        Selected Group Properties:
                                    </h4>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full text-xs border rounded border-purple-200">
                                            <thead className="bg-purple-100">
                                                <tr>
                                                    <th className="px-1 py-0.5 text-left font-medium border-b text-purple-900">Group Name</th>
                                                    <th className="px-1 py-0.5 text-left font-medium border-b text-purple-900">UOM</th>
                                                    <th className="px-1 py-0.5 text-left font-medium border-b text-purple-900">Value</th>
                                                    <th className="px-1 py-0.5 text-left font-medium border-b text-purple-900">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {newComponent.selectedGroups.map((item, index) => (
                                                    <tr key={`group-${index}-${item.name}`} className="border-b border-purple-200 last:border-b-0">
                                                        <td className="px-1 py-0.5 text-purple-800 truncate max-w-[80px]">{item.name}</td>
                                                        <td className="px-1 py-0.5 text-purple-700">{item.uom}</td>
                                                        <td className="px-1 py-0.5 text-purple-700">
                                                            <input
                                                                type="number"
                                                                value={item.editableDefaultValue || item.defaultValue}
                                                                onChange={(e) => handleDefaultValueChange(item.name, e.target.value, false)}
                                                                className="w-16 px-1 py-0.5 border border-gray-300 rounded text-xs"
                                                                min={item.minValue}
                                                                max={item.maxValue}
                                                                step="0.1"
                                                            />
                                                        </td>
                                                        <td className="px-1 py-0.5">
                                                            <button
                                                                type="button"
                                                                onClick={() => handlePropertySelection(item)}
                                                                className="text-red-500 hover:text-red-700 text-xs"
                                                            >
                                                                Remove
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Property Type Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Property Type <span className="text-red-500">*</span>
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setPropertyType("global")}
                                        className={`px-3 md:px-4 py-2 rounded-lg font-medium transition-colors text-xs md:text-sm ${propertyType === "global"
                                            ? "bg-blue-600 text-white border-2 border-blue-600"
                                            : "bg-gray-100 text-gray-700 border-2 border-gray-300 hover:bg-gray-200"
                                            }`}
                                    >
                                        Global Properties
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPropertyType("group")}
                                        className={`px-3 md:px-4 py-2 rounded-lg font-medium transition-colors text-xs md:text-sm ${propertyType === "group"
                                            ? "bg-purple-600 text-white border-2 border-purple-600"
                                            : "bg-gray-100 text-gray-700 border-2 border-gray-300 hover:bg-gray-200"
                                            }`}
                                    >
                                        Group Properties
                                    </button>
                                </div>
                            </div>

                            {/* Properties Section */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {propertyType === "global" ? "Global Properties" : "Group Properties"}
                                    <span className="text-gray-500 ml-1">
                                        ({propertyType === "global" ? newComponent.selectedProperties.length : newComponent.selectedGroups.length} selected)
                                    </span>
                                </label>

                                {(propertyType === "global" ? globalPropertyOptions : groupPropertyOptions).length > 0 ? (
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-600">
                                                Select {propertyType === "global" ? "individual properties" : "property groups"}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={handleSelectAllProperties}
                                                className={`text-xs font-medium ${propertyType === "global"
                                                    ? "text-blue-600 hover:text-blue-800"
                                                    : "text-purple-600 hover:text-purple-800"
                                                    }`}
                                            >
                                                {propertyType === "global"
                                                    ? (newComponent.selectedProperties.length === globalPropertyOptions.length ? "Deselect All" : "Select All")
                                                    : (newComponent.selectedGroups.length === groupPropertyOptions.length ? "Deselect All" : "Select All")
                                                }
                                            </button>
                                        </div>

                                        {/* Properties List */}
                                        <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
                                            <div className="divide-y divide-gray-200">
                                                {(propertyType === "global" ? globalPropertyOptions : groupPropertyOptions).map((item, index) => {
                                                    const isSelected = propertyType === "global"
                                                        ? newComponent.selectedProperties.some(p => p.name === item.name)
                                                        : newComponent.selectedGroups.some(g => g.name === item.name);

                                                    return (
                                                        <div
                                                            key={`${propertyType}-${index}-${item.name}`}
                                                            className={`p-2 cursor-pointer transition-colors ${isSelected
                                                                ? propertyType === "global"
                                                                    ? "bg-blue-50 border-l-4 border-l-blue-500"
                                                                    : "bg-purple-50 border-l-4 border-l-purple-500"
                                                                : "hover:bg-gray-50"
                                                                }`}
                                                            onClick={() => handlePropertySelection(item)}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <div className={`w-4 h-4 rounded border flex items-center justify-center ${isSelected
                                                                    ? propertyType === "global"
                                                                        ? "bg-blue-500 border-blue-500"
                                                                        : "bg-purple-500 border-purple-500"
                                                                    : "border-gray-300"
                                                                    }`}>
                                                                    {isSelected && (
                                                                        <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                    )}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex justify-between items-start">
                                                                        <span className="font-medium text-gray-900 text-sm truncate">{item.displayName}</span>
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 mt-0.5 grid grid-cols-3 gap-1">
                                                                        <span>Min: {item.minValue}</span>
                                                                        <span>Max: {item.maxValue}</span>
                                                                        <span>Def: {item.defaultValue}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-3 text-gray-500 bg-gray-50 rounded-lg border border-gray-200 text-xs">
                                        {propertyType === "global"
                                            ? "No global properties available. Please add global properties first."
                                            : "No group properties available. Please add group properties first."
                                        }
                                    </div>
                                )}
                            </div>

                            {/* Selection Summary */}
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                                <div className="flex items-start gap-2">
                                    <svg className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div>
                                        <h4 className="text-xs font-medium text-yellow-800">Selection Summary</h4>
                                        <p className="text-xs text-yellow-700 mt-0.5">
                                            Total selected: <span className="font-semibold">{newComponent.selectedProperties.length + newComponent.selectedGroups.length}</span> items
                                            {newComponent.selectedProperties.length > 0 && (
                                                <span> {newComponent.selectedProperties.length} global properties</span>
                                            )}
                                            {newComponent.selectedGroups.length > 0 && (
                                                <span>{newComponent.selectedProperties.length > 0 ? ', ' : ' ('}{newComponent.selectedGroups.length} group properties</span>
                                            )}
                                            {(newComponent.selectedProperties.length > 0 || newComponent.selectedGroups.length > 0) && <span>)</span>}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-2 justify-end pt-4 border-t border-gray-200 mt-4">
                            <button
                                onClick={() => setShowAddComponentModal(false)}
                                className="px-3 md:px-4 py-2 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors text-xs md:text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddComponent}
                                disabled={!newComponent.name.trim() || (newComponent.selectedProperties.length === 0 && newComponent.selectedGroups.length === 0)}
                                className={`px-3 md:px-4 py-2 rounded-lg font-medium component-btn flex items-center gap-1 text-xs md:text-sm ${!newComponent.name.trim() || (newComponent.selectedProperties.length === 0 && newComponent.selectedGroups.length === 0)
                                    ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                                    : "bg-green-600 text-white hover:bg-green-700"
                                    }`}
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Add Component
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && componentToDelete && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-sm w-full transform transition-all">
                        <div className="p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                    <svg className="w-4 h-4 md:w-5 md:h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-gray-900">Delete Component</h3>
                                    <p className="text-gray-600 text-sm mt-0.5">
                                        Are you sure you want to delete component "{componentToDelete.name}"? This action cannot be undone.
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-2 mt-4">
                                <button
                                    onClick={handleDeleteCancel}
                                    className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-200 text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteConfirm}
                                    className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors duration-200 flex items-center justify-center text-sm"
                                >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                    </svg>
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Component Popup */}
            {showEditComponentPopup && editingComponent && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 md:p-4">
                    <div className="bg-white rounded-xl p-3 md:p-4 max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl transform transition-all">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 md:w-8 md:h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 md:w-4 md:h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                        />
                                    </svg>
                                </div>
                                <h3 className="text-base md:text-lg font-bold text-gray-900">Edit Component</h3>
                            </div>
                            <button
                                onClick={handleEditComponentCancel}
                                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Component Name *</label>
                                <input
                                    type="text"
                                    value={editedComponentData.name || ""}
                                    onChange={(e) => handleComponentFieldChange("name", e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={editedComponentData.cost || ""}
                                        onChange={(e) => handleComponentFieldChange("cost", e.target.value)}
                                        placeholder="0.00"
                                        className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                                    <div className="relative w-full">
                                        <select
                                            value={editedComponentData.costUnit || "INR"}
                                            onChange={(e) => handleComponentFieldChange("costUnit", e.target.value)}
                                            className="w-full appearance-none px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-xl bg-white text-sm md:text-base font-medium shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        >
                                            {currencies.map((curr) => (
                                                <option key={curr.code} value={curr.code}>
                                                    {curr.symbol} {curr.code}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="absolute inset-y-0 right-2 md:right-3 flex items-center pointer-events-none">
                                            <svg
                                                className="w-4 h-4 md:w-5 md:h-5 text-gray-500"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Gravity Field */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Specific Gravity</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={editedComponentData.gravity || ""}
                                    onChange={(e) => handleComponentFieldChange("gravity", e.target.value)}
                                    placeholder="0.00"
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                                />
                            </div>

                            {/* ---------- Global Properties Section ---------- */}
                            {(editedComponentData.selectedProperties || []).length > 0 && (
                                <div className="border rounded-lg p-2 bg-blue-50 border-blue-200 mb-4">
                                    <h4 className="text-xs font-medium mb-1 text-blue-900">
                                        Selected Global Properties:
                                    </h4>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full text-xs border rounded border-blue-200">
                                            <thead className="bg-blue-100">
                                                <tr>
                                                    <th className="px-1 py-0.5 text-left font-medium border-b text-blue-900">Property</th>
                                                    <th className="px-1 py-0.5 text-left font-medium border-b text-blue-900">UOM</th>
                                                    <th className="px-1 py-0.5 text-left font-medium border-b text-blue-900">Value</th>
                                                    <th className="px-1 py-0.5 text-left font-medium border-b text-blue-900">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(editedComponentData.selectedProperties || []).map((item, index) => (
                                                    <tr
                                                        key={`edit-global-${index}-${item.name}`}
                                                        className="border-b border-blue-200 last:border-b-0"
                                                    >
                                                        <td className="px-1 py-0.5 text-blue-800 truncate max-w-[80px]">{item.name}</td>
                                                        <td className="px-1 py-0.5 text-blue-700">{item.uom}</td>
                                                        <td className="px-1 py-0.5 text-blue-700">
                                                            <input
                                                                type="number"
                                                                value={item.editableDefaultValue || item.defaultValue}
                                                                onChange={(e) =>
                                                                    handleEditDefaultValueChange(item.name, e.target.value, true)
                                                                }
                                                                className="w-16 px-1 py-0.5 border border-gray-300 rounded text-xs"
                                                                min={item.minValue}
                                                                max={item.maxValue}
                                                                step="0.1"
                                                            />
                                                        </td>
                                                        <td className="px-1 py-0.5">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleEditPropertySelection(item)}
                                                                className="text-red-500 hover:text-red-700 text-xs"
                                                            >
                                                                Remove
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* ---------- Group Properties Section ---------- */}
                            {(editedComponentData.selectedGroups || []).length > 0 && (
                                <div className="border rounded-lg p-2 bg-purple-50 border-purple-200">
                                    <h4 className="text-xs font-medium mb-1 text-purple-900">
                                        Selected Group Properties:
                                    </h4>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full text-xs border rounded border-purple-200">
                                            <thead className="bg-purple-100">
                                                <tr>
                                                    <th className="px-1 py-0.5 text-left font-medium border-b text-purple-900">Group Name</th>
                                                    <th className="px-1 py-0.5 text-left font-medium border-b text-purple-900">UOM</th>
                                                    <th className="px-1 py-0.5 text-left font-medium border-b text-purple-900">Value</th>
                                                    <th className="px-1 py-0.5 text-left font-medium border-b text-purple-900">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(editedComponentData.selectedGroups || []).map((item, index) => (
                                                    <tr
                                                        key={`edit-group-${index}-${item.name}`}
                                                        className="border-b border-purple-200 last:border-b-0"
                                                    >
                                                        <td className="px-1 py-0.5 text-purple-800 truncate max-w-[80px]">{item.name}</td>
                                                        <td className="px-1 py-0.5 text-purple-700">{item.uom}</td>
                                                        <td className="px-1 py-0.5 text-purple-700">
                                                            <input
                                                                type="number"
                                                                value={item.editableDefaultValue || item.defaultValue}
                                                                onChange={(e) =>
                                                                    handleEditDefaultValueChange(item.name, e.target.value, false)
                                                                }
                                                                className="w-16 px-1 py-0.5 border border-gray-300 rounded text-xs"
                                                                min={item.minValue}
                                                                max={item.maxValue}
                                                                step="0.1"
                                                            />
                                                        </td>
                                                        <td className="px-1 py-0.5">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleEditPropertySelection(item)}
                                                                className="text-red-500 hover:text-red-700 text-xs"
                                                            >
                                                                Remove
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Property Type <span className="text-red-500">*</span>
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setPropertyType("global")}
                                        className={`px-3 md:px-4 py-2 rounded-lg font-medium transition-colors text-xs md:text-sm ${propertyType === "global"
                                            ? "bg-blue-600 text-white border-2 border-blue-600"
                                            : "bg-gray-100 text-gray-700 border-2 border-gray-300 hover:bg-gray-200"
                                            }`}
                                    >
                                        Global Properties
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPropertyType("group")}
                                        className={`px-3 md:px-4 py-2 rounded-lg font-medium transition-colors text-xs md:text-sm ${propertyType === "group"
                                            ? "bg-purple-600 text-white border-2 border-purple-600"
                                            : "bg-gray-100 text-gray-700 border-2 border-gray-300 hover:bg-gray-200"
                                            }`}
                                    >
                                        Group Properties
                                    </button>
                                </div>
                            </div>

                            {/* Properties Section - Edit Mode */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {propertyType === "global" ? "Global Properties" : "Group Properties"}
                                    <span className="text-gray-500 ml-1">
                                        ({propertyType === "global" ? (editedComponentData.selectedProperties || []).length : (editedComponentData.selectedGroups || []).length} selected)
                                    </span>
                                </label>

                                {(propertyType === "global" ? globalPropertyOptions : groupPropertyOptions).length > 0 ? (
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-600">
                                                Select {propertyType === "global" ? "individual properties" : "property groups"}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={handleEditSelectAllProperties}
                                                className={`text-xs font-medium ${propertyType === "global"
                                                    ? "text-blue-600 hover:text-blue-800"
                                                    : "text-purple-600 hover:text-purple-800"
                                                    }`}
                                            >
                                                {propertyType === "global"
                                                    ? ((editedComponentData.selectedProperties || []).length === globalPropertyOptions.length ? "Deselect All" : "Select All")
                                                    : ((editedComponentData.selectedGroups || []).length === groupPropertyOptions.length ? "Deselect All" : "Select All")
                                                }
                                            </button>
                                        </div>

                                        <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
                                            <div className="divide-y divide-gray-200">
                                                {(propertyType === "global" ? globalPropertyOptions : groupPropertyOptions).map((item, index) => {
                                                    const currentItems = propertyType === "global"
                                                        ? (editedComponentData.selectedProperties || [])
                                                        : (editedComponentData.selectedGroups || []);
                                                    const isSelected = currentItems.some(p => p.name === item.name);

                                                    return (
                                                        <div
                                                            key={`edit-${propertyType}-prop-${index}-${item.name}`}
                                                            className={`p-2 cursor-pointer transition-colors ${isSelected
                                                                ? propertyType === "global"
                                                                    ? "bg-blue-50 border-l-4 border-l-blue-500"
                                                                    : "bg-purple-50 border-l-4 border-l-purple-500"
                                                                : "hover:bg-gray-50"
                                                                }`}
                                                            onClick={() => handleEditPropertySelection(item)}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <div className={`w-4 h-4 rounded border flex items-center justify-center ${isSelected
                                                                    ? propertyType === "global"
                                                                        ? "bg-blue-500 border-blue-500"
                                                                        : "bg-purple-500 border-purple-500"
                                                                    : "border-gray-300"
                                                                    }`}>
                                                                    {isSelected && (
                                                                        <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                    )}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex justify-between items-start">
                                                                        <span className="font-medium text-gray-900 text-sm truncate">{item.displayName}</span>
                                                                    </div>
                                                                    <div className="text-xs text-gray-500 mt-0.5 grid grid-cols-3 gap-1">
                                                                        <span>Min: {item.minValue}</span>
                                                                        <span>Max: {item.maxValue}</span>
                                                                        <span>Def: {item.defaultValue}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-3 text-gray-500 bg-gray-50 rounded-lg border border-gray-200 text-xs">
                                        {propertyType === "global"
                                            ? "No global properties available"
                                            : "No group properties available"
                                        }
                                    </div>
                                )}
                            </div>

                            {/* Selection Summary - Edit Mode */}
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                                <div className="flex items-start gap-2">
                                    <svg className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div>
                                        <h4 className="text-xs font-medium text-yellow-800">Selection Summary</h4>
                                        <p className="text-xs text-yellow-700 mt-0.5">
                                            Total selected: <span className="font-semibold">{(editedComponentData.selectedProperties || []).length + (editedComponentData.selectedGroups || []).length}</span> items
                                            {(editedComponentData.selectedProperties || []).length > 0 && (
                                                <span> ({(editedComponentData.selectedProperties || []).length} global properties</span>
                                            )}
                                            {(editedComponentData.selectedGroups || []).length > 0 && (
                                                <span>{(editedComponentData.selectedProperties || []).length > 0 ? ', ' : ' ('}{(editedComponentData.selectedGroups || []).length} group properties</span>
                                            )}
                                            {((editedComponentData.selectedProperties || []).length > 0 || (editedComponentData.selectedGroups || []).length > 0) && <span>)</span>}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-2 justify-end pt-4 border-t border-gray-200 mt-4">
                            <button
                                onClick={handleEditComponentCancel}
                                className="px-3 md:px-4 py-2 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors text-xs md:text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleEditComponentSave}
                                className="component-btn"
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Group Popup */}
            {showAddGroupPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 md:p-4">
                    <div className="bg-white rounded-xl p-3 md:p-4 max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl transform transition-all">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 md:w-8 md:h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 md:w-4 md:h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                    </svg>
                                </div>
                                <h3 className="text-base md:text-lg font-bold text-gray-900">Create New Group</h3>
                            </div>
                            <button
                                onClick={() => setShowAddGroupPopup(false)}
                                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {selectedComponents.length > 0 && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 md:p-3 mb-4">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                                    <div className="flex-1">
                                        <h3 className="text-sm md:text-base font-semibold text-blue-900 mb-1">
                                            Selected Components ({selectedComponents.length})
                                        </h3>
                                        <div className="flex flex-wrap gap-1">
                                            {selectedComponents.slice(0, 6).map((componentKey) => {
                                                const [type, index] = componentKey.split("-")
                                                const numericIndex = Number(index)
                                                let component = null

                                                if (type === "api") {
                                                    if (numericIndex >= 0 && numericIndex < apiComponents.length) {
                                                        component = apiComponents[numericIndex]
                                                    }
                                                } else if (type === "added") {
                                                    if (numericIndex >= 0 && numericIndex < addedComponents.length) {
                                                        component = addedComponents[numericIndex]
                                                    }
                                                }

                                                if (!component) return null

                                                return (
                                                    <span
                                                        key={`selected-${componentKey}`}
                                                        className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-0.5"
                                                    >
                                                        <span className="truncate max-w-[80px]">{component.name}</span> ({type === "api" ? "API" : "Added"})
                                                        <button
                                                            onClick={() => handleComponentSelection(componentKey)}
                                                            className="text-blue-600 hover:text-blue-800 ml-0.5 text-xs"
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                )
                                            })}
                                            {selectedComponents.length > 6 && (
                                                <span className="bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full text-xs font-medium">
                                                    +{selectedComponents.length - 6} more
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-right mt-2 md:mt-0">
                                        <div className="text-xs text-blue-700">
                                            Group Name: <span className="font-semibold truncate">{groupName || "Not set"}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Group Name *</label>
                            <input
                                type="text"
                                value={groupName}
                                onChange={(e) => setGroupName(e.target.value)}
                                placeholder="Enter group name"
                                className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm"
                            />
                        </div>

                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm md:text-base font-semibold text-gray-900">
                                Select Components ({selectedComponents.length} selected)
                            </h4>
                            <button
                                onClick={handleSelectAllComponents}
                                className="px-2 md:px-3 py-1 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors text-xs"
                            >
                                {selectedComponents.length === (apiComponents.length + addedComponents.length) ? "Deselect All" : "Select All"}
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg">
                            <div className="p-2 md:p-3">
                                <h4 className="text-sm md:text-base font-semibold text-gray-900 mb-3">All Available Components</h4>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 text-xs">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-1 md:px-2 py-1 text-left text-xs font-medium text-gray-700 uppercase w-6">Select</th>
                                                <th className="px-1 md:px-2 py-1 text-left text-xs font-medium text-gray-700 uppercase">Component</th>
                                                <th className="px-1 md:px-2 py-1 text-right text-xs font-medium text-gray-700 uppercase">
                                                    Cost ({getCurrencySymbol()})
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {/* API Components */}
                                            {apiComponents.map((component, index) => {
                                                const componentKey = `api-${index}`
                                                const isSelected = selectedComponents.includes(componentKey)

                                                return (
                                                    <tr
                                                        key={generateUniqueKey(component, index, 'api')}
                                                        className={`hover:bg-gray-50 ${isSelected ? "bg-blue-50" : ""}`}
                                                    >
                                                        <td className="px-1 md:px-2 py-1 whitespace-nowrap">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => handleComponentSelection(componentKey)}
                                                                className="w-3 h-3 text-blue-600 rounded focus:ring-blue-500"
                                                            />
                                                        </td>
                                                        <td className="px-1 md:px-2 py-1 whitespace-nowrap font-medium text-gray-900 text-xs truncate max-w-[120px] md:max-w-none">{component.name}</td>
                                                        <td className="px-1 md:px-2 py-1 whitespace-nowrap text-right text-gray-900 text-xs">
                                                            {getCurrencySymbol()}
                                                            {Number(component.cost || 0).toFixed(2)}
                                                        </td>
                                                    </tr>
                                                )
                                            })}

                                            {/* Added Components */}
                                            {addedComponents.map((component, index) => {
                                                const componentKey = `added-${index}`
                                                const isSelected = selectedComponents.includes(componentKey)

                                                return (
                                                    <tr
                                                        key={generateUniqueKey(component, index, 'added')}
                                                        className={`hover:bg-gray-50 ${isSelected ? "bg-blue-50" : ""}`}
                                                    >
                                                        <td className="px-1 md:px-2 py-1 whitespace-nowrap">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => handleComponentSelection(componentKey)}
                                                                className="w-3 h-3 text-blue-600 rounded focus:ring-blue-500"
                                                            />
                                                        </td>
                                                        <td className="px-1 md:px-2 py-1 whitespace-nowrap font-medium text-gray-900 text-xs truncate max-w-[120px] md:max-w-none">{component.name}</td>
                                                        <td className="px-1 md:px-2 py-1 whitespace-nowrap text-right text-gray-900 text-xs">
                                                            {getCurrencySymbol()}
                                                            {Number(component.cost || 0).toFixed(2)}
                                                        </td>
                                                        <td className="px-1 md:px-2 py-1 whitespace-nowrap text-right text-gray-900 text-xs">
                                                            {component.gravity || "0"}
                                                        </td>
                                                        <td className="px-1 md:px-2 py-1 whitespace-nowrap text-gray-600">
                                                            <span className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full text-xs font-medium">
                                                                Added
                                                            </span>
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-2 justify-end pt-4 border-t border-gray-200 mt-4">
                            <button
                                onClick={() => setShowAddGroupPopup(false)}
                                className="px-3 md:px-4 py-2 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors text-xs md:text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateGroup}
                                className="property-btn"
                            >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Create Group
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Navigation
                currentUser={currentUser}
                activeTab={activeTab}
                onTabChange={onTabChange}
                onLogout={handleLogoutClick}
                onShowUserManagement={onShowUserManagement}
                sidebarOpen={sidebarOpen}
                onToggleSidebar={onToggleSidebar}
            />

            <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-0"}`}>
                <Header currentUser={currentUser} onTestAPI={() => { }} onToggleSidebar={onToggleSidebar} />

                <main className="flex-1 p-3 md:p-4 lg:p-6 overflow-auto">
                    <div className="max-w-7xl mx-auto w-full">
                        <div className="mb-4 md:mb-6 lg:mb-8">
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 md:gap-4">

                                <div className="flex-1">
                                    <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">Components Management</h1>
                                    <p className="text-gray-600 mt-1 md:mt-2 text-xs md:text-sm lg:text-base">
                                        All components from database ({apiComponents.length + addedComponents.length} total: {apiComponents.length} API, {addedComponents.length} added)
                                        <span className="ml-1 md:ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                                            Currency: {getCurrencySymbol()} {currency}
                                        </span>
                                        {(loadingComponents || loadingAddedComponents) && (
                                            <span className="ml-1 md:ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                                                Loading...
                                            </span>
                                        )}
                                    </p>
                                </div>

                                <div className="flex flex-wrap gap-2 sm:gap-3 mt-2 lg:mt-0">
                                    <button
                                        onClick={() => setShowExcelImportPopup(true)}
                                        className="component-btn"
                                    >
                                        <svg className="w-3 h-3 md:w-4 md:h-4 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                                            />
                                        </svg>
                                        <span className="hidden sm:inline">Import</span>
                                    </button>

                                    <button
                                        onClick={() => setShowAddComponentModal(true)}
                                        className="component-btn"
                                    >
                                        <svg className="w-3 h-3 md:w-4 md:h-4 lg:w-5 lg:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                            />
                                        </svg>
                                        <span className="hidden sm:inline">Component</span>
                                    </button>
                                </div>
                            </div>

                            {(apiComponents.length > 0 || apiGroupReports.length > 0) && (
                                <div className="flex border-b border-gray-200 mt-4 md:mt-6 overflow-x-auto">
                                    <button
                                        onClick={() => {
                                            setShowGlobalReport(true)
                                            setShowGroupReport(false)
                                        }}
                                        className={`px-3 md:px-4 py-2 font-medium text-xs md:text-sm border-b-2 transition-colors whitespace-nowrap ${showGlobalReport
                                            ? "border-blue-500 text-blue-600"
                                            : "border-transparent text-gray-500 hover:text-gray-700"
                                            }`}
                                    >
                                        {/* Global Component ({apiComponents.length + addedComponents.length}) */}
                                        Global Component
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowGroupReport(true)
                                            setShowGlobalReport(false)
                                        }}
                                        className={`px-3 md:px-4 py-2 font-medium text-xs md:text-sm border-b-2 transition-colors whitespace-nowrap ${showGroupReport
                                            ? "border-purple-500 text-purple-600"
                                            : "border-transparent text-gray-500 hover:text-gray-700"
                                            }`}
                                    >
                                        {/* Group Component ({apiGroupReports.length}) */}
                                        Group Component
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Global Report Section */}
                        {showGlobalReport && (
                            <div className="space-y-4 md:space-y-6">
                                <div className="bg-white rounded-xl p-4 md:p-6 shadow-lg border border-gray-200">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4 mb-4 md:mb-6">
                                        <div className="flex-1">
                                            <h2 className="text-lg md:text-xl font-bold text-gray-900">Global Components Report</h2>
                                            <p className="text-gray-600 mt-1 text-xs md:text-sm">
                                                All components from database ({apiComponents.length + addedComponents.length} components)
                                            </p>
                                        </div>
                                        <div className="flex gap-2 md:gap-3 mt-2 md:mt-0">
                                            {selectedForBulkDelete.length > 0 && (
                                                <button
                                                    onClick={handleBulkDelete}
                                                    className="px-2 md:px-3 py-1 md:py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1 text-xs md:text-sm"
                                                >
                                                    <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                        />
                                                    </svg>
                                                    <span>Delete ({selectedForBulkDelete.length})</span>
                                                </button>
                                            )}
                                            <button
                                                onClick={handleAddGroupClick}
                                                className="property-btn"
                                            >
                                                <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                                <span>Group</span>
                                            </button>
                                            <button
                                                onClick={downloadGlobalReportCSV}
                                                className="save-all-btn"
                                            >
                                                <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                    />
                                                </svg>
                                                <span>Export CSV</span>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="bg-gray-50 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                                            <div className="md:col-span-2 lg:col-span-1">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                                                <input
                                                    type="text"
                                                    placeholder="Search components..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {loadingComponents || loadingAddedComponents ? (
                                        <div className="text-center py-8 md:py-12">
                                            <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                                                <svg className="w-6 h-6 md:w-8 md:h-8 text-blue-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                            </div>
                                            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">Loading Components</h3>
                                            <p className="text-gray-500 text-sm">
                                                Loading: {loadingComponents ? 'API Components' : ''}
                                                {loadingComponents && loadingAddedComponents ? ' and ' : ''}
                                                {loadingAddedComponents ? 'Added Components' : ''}
                                            </p>
                                        </div>
                                    ) : filteredUniqueComponents.length === 0 ? (
                                        <div className="text-center py-8 md:py-12">
                                            <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                                                <svg className="w-6 h-6 md:w-8 md:h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                    />
                                                </svg>
                                            </div>
                                            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">No components found</h3>
                                            <p className="text-gray-500 text-sm mb-4 md:mb-6">Try adjusting your filters to find components</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedForBulkDelete.length === filteredUniqueComponents.length && filteredUniqueComponents.length > 0}
                                                        onChange={handleSelectAllForBulkDelete}
                                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm text-gray-700">
                                                        Select All ({selectedForBulkDelete.length} selected)
                                                    </span>
                                                </div>
                                                {selectedForBulkDelete.length > 0 && (
                                                    <button
                                                        onClick={handleBulkDelete}
                                                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1 text-sm"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                            />
                                                        </svg>
                                                        Delete Selected ({selectedForBulkDelete.length})
                                                    </button>
                                                )}
                                            </div>

                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className="px-2 md:px-4 lg:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-8">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedForBulkDelete.length === filteredUniqueComponents.length && filteredUniqueComponents.length > 0}
                                                                    onChange={handleSelectAllForBulkDelete}
                                                                    className="w-3 h-3 text-blue-600 rounded focus:ring-blue-500"
                                                                />
                                                            </th>
                                                            <th className="px-2 md:px-4 lg:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                                                Component Name
                                                            </th>
                                                            <th className="px-2 md:px-4 lg:px-6 py-2 md:py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                                                                Cost ({getCurrencySymbol()})
                                                            </th>
                                                            <th className="px-2 md:px-4 lg:px-6 py-2 md:py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                                                Actions
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {currentGlobalItems.map((component, index) => {
                                                            const isAddedComponent = component.source === "manual";
                                                            const isApiComponent = component.source === "api";
                                                            const isExcelImported = component.source === "excel-imported";
                                                            const componentId = component._id || component.id;
                                                            const isSelectedForBulkDelete = selectedForBulkDelete.includes(componentId);

                                                            const selectedPropertyNames = getSelectedPropertyNames(component);

                                                            return (
                                                                <tr key={componentId || generateUniqueKey(component, index, 'global')} className="hover:bg-gray-50 transition-colors">
                                                                    <td className="px-2 md:px-4 lg:px-6 py-3 md:py-4 whitespace-nowrap">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isSelectedForBulkDelete}
                                                                            onChange={() => handleBulkDeleteSelection(componentId)}
                                                                            className="w-3 h-3 text-blue-600 rounded focus:ring-blue-500"
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 md:px-4 lg:px-6 py-3 md:py-4 whitespace-nowrap">
                                                                        <div className="flex items-center">
                                                                            <span className="font-medium text-gray-900 text-xs md:text-sm truncate max-w-[120px] md:max-w-none">{component.name}</span>
                                                                        </div>
                                                                    </td>

                                                                    <td className="px-2 md:px-4 lg:px-6 py-3 md:py-4 whitespace-nowrap text-right">
                                                                        <span className="text-gray-900 font-semibold text-xs md:text-sm">
                                                                            {getCurrencySymbol(component.costUnit)}
                                                                            {Number(component.cost || 0).toFixed(2)}
                                                                        </span>
                                                                    </td>


                                                                    <td className="px-2 md:px-4 lg:px-6 py-3 md:py-4 whitespace-nowrap">
                                                                        <div className="flex gap-1 md:gap-2">
                                                                            <button
                                                                                onClick={() => handleEditComponent(component, isAddedComponent)}
                                                                                className="px-2 md:px-3 py-1 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-1 text-xs"
                                                                                title="Edit"
                                                                            >
                                                                                <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                </svg>
                                                                                <span className="hidden sm:inline">Edit</span>
                                                                            </button>
                                                                            <button
                                                                                onClick={() => handleDeleteComponent(component, isAddedComponent)}
                                                                                className="px-2 md:px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1 text-xs"
                                                                                title="Delete"
                                                                            >
                                                                                <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                </svg>
                                                                                <span className="hidden sm:inline">Delete</span>
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>

                                            {totalPages > 1 && (
                                                <div className="mt-4 md:mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 md:gap-4">
                                                    <div className="text-xs md:text-sm text-gray-600">
                                                        Showing {startIndex + 1} to {Math.min(endIndex, filteredUniqueComponents.length)} of{" "}
                                                        {filteredUniqueComponents.length} entries
                                                    </div>

                                                    <div className="flex items-center gap-1 md:gap-2">
                                                        <button
                                                            onClick={() => handlePageChange(currentPage - 1)}
                                                            disabled={currentPage === 1}
                                                            className={`px-2 md:px-3 py-1 md:py-2 rounded-lg border border-gray-300 text-xs md:text-sm font-medium transition-colors ${currentPage === 1
                                                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                                : "bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                                                                }`}
                                                        >
                                                            Previous
                                                        </button>

                                                        <div className="flex items-center gap-1">
                                                            {getPageNumbers().map((page) => (
                                                                <button
                                                                    key={`page-${page}`}
                                                                    onClick={() => handlePageChange(page)}
                                                                    className={`px-2 md:px-3 py-1 md:py-2 rounded-lg border text-xs md:text-sm font-medium transition-colors ${currentPage === page
                                                                        ? "bg-blue-600 text-white border-blue-600"
                                                                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                                                                        }`}
                                                                >
                                                                    {page}
                                                                </button>
                                                            ))}
                                                        </div>

                                                        <button
                                                            onClick={() => handlePageChange(currentPage + 1)}
                                                            disabled={currentPage === totalPages}
                                                            className={`px-2 md:px-3 py-1 md:py-2 rounded-lg border border-gray-300 text-xs md:text-sm font-medium transition-colors ${currentPage === totalPages
                                                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                                : "bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                                                                }`}
                                                        >
                                                            Next
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Group Report Section */}
                        {showGroupReport && (
                            <div className="space-y-4 md:space-y-6">
                                <div className="bg-white rounded-xl p-4 md:p-6 shadow-lg border border-gray-200">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4 mb-4 md:mb-6">
                                        <div className="flex-1">
                                            <h2 className="text-lg md:text-xl font-bold text-gray-900">Group Report</h2>
                                            <p className="text-gray-600 mt-1 text-xs md:text-sm">
                                                Manage and export grouped component sets ({apiGroupReports.length} groups)
                                            </p>
                                        </div>
                                        <button
                                            onClick={handleAddGroupClick}
                                            className="property-btn"
                                        >
                                            <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                            <span>Group</span>
                                        </button>
                                    </div>

                                    {loadingGroupReports ? (
                                        <div className="text-center py-8 md:py-12">
                                            <div className="w-12 h-12 md:w-16 md:h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                                                <svg className="w-6 h-6 md:w-8 md:h-8 text-purple-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                            </div>
                                            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">Loading Group Reports</h3>
                                            <p className="text-gray-500 text-sm">Please wait while we fetch your group reports...</p>
                                        </div>
                                    ) : apiGroupReports.length === 0 ? (
                                        <div className="text-center py-8 md:py-12">
                                            <div className="w-12 h-12 md:w-16 md:h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                                                <svg className="w-6 h-6 md:w-8 md:h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                                    />
                                                </svg>
                                            </div>
                                            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-1 md:mb-2">No groups created yet</h3>
                                            <p className="text-gray-500 text-sm mb-4 md:mb-6">Create your first group to organize component sets</p>
                                            <button
                                                onClick={handleAddGroupClick}
                                                className="px-4 md:px-6 py-2 md:py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto text-sm"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                </svg>
                                                Create Your First Group
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="space-y-4 md:space-y-6">
                                            {apiGroupReports.map((report) => (
                                                <div key={report._id} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                                                    <div
                                                        className="p-3 md:p-4 cursor-pointer transition-colors hover:bg-gray-100"
                                                        onClick={() => toggleGroupReportExpansion(report._id)}
                                                    >
                                                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 md:gap-3">
                                                            <div className="flex items-center gap-2 md:gap-3">
                                                                <div className="w-8 h-8 md:w-10 md:h-10 bg-white rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                                                                    <svg
                                                                        className="w-4 h-4 md:w-5 md:h-5 text-green-600"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={2}
                                                                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                                                                        />
                                                                    </svg>
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <h3 className="text-base md:text-lg font-bold text-gray-900 truncate">{report.name}</h3>
                                                                    <div className="flex flex-wrap items-center gap-1 md:gap-2 mt-1">
                                                                        <span className="bg-white text-gray-700 px-2 py-1 rounded text-xs font-medium">
                                                                            {report.components.length} components
                                                                        </span>
                                                                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                                                                            Total: {report.currency}{report.totalCost.toFixed(2)}
                                                                        </span>
                                                                        <span className="text-gray-500 text-xs">
                                                                            {new Date(report.createdAt).toLocaleDateString()}
                                                                        </span>
                                                                    </div>
                                                                    {report.description && (
                                                                        <p className="text-gray-600 text-xs md:text-sm mt-1 md:mt-2 truncate">{report.description}</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex gap-1 md:gap-2 mt-2 lg:mt-0">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        handleEditGroup(report)
                                                                    }}
                                                                    className="px-2 md:px-3 py-1 md:py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium transition-colors flex items-center gap-1 text-xs md:text-sm flex-1 lg:flex-none justify-center"
                                                                >
                                                                    <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                    </svg>
                                                                    <span>Edit</span>
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        handleDeleteGroupReport(report._id)
                                                                    }}
                                                                    className="px-2 md:px-3 py-1 md:py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors flex items-center gap-1 text-xs md:text-sm flex-1 lg:flex-none justify-center"
                                                                >
                                                                    <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth={2}
                                                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                                        />
                                                                    </svg>
                                                                    <span>Delete</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {expandedGroupReport === report._id && (
                                                        <div className="border-t border-gray-200 bg-white">
                                                            <div className="p-3 md:p-4 lg:p-6">
                                                                <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Group Components</h4>
                                                                <div className="overflow-x-auto">
                                                                    <table className="min-w-full divide-y divide-gray-200">
                                                                        <thead className="bg-gray-50">
                                                                            <tr>
                                                                                <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                                                                    Component Name
                                                                                </th>
                                                                                <th className="px-2 md:px-4 py-2 md:py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                                                                                    Cost ({report.currency})
                                                                                </th>
                                                                                <th className="px-2 md:px-4 py-2 md:py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                                                                                    Volume
                                                                                </th>
                                                                                <th className="px-2 md:px-4 py-2 md:py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                                                                                    Percentage
                                                                                </th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                                            {report.components.map((component, index) => {
                                                                                const selectedPropertyNames = [];

                                                                                if (component.selectedProperties && component.selectedProperties.length > 0) {
                                                                                    component.selectedProperties.forEach(prop => {
                                                                                        selectedPropertyNames.push(prop.name);
                                                                                    });
                                                                                }

                                                                                if (component.selectedGroups && component.selectedGroups.length > 0) {
                                                                                    component.selectedGroups.forEach(group => {
                                                                                        selectedPropertyNames.push(`${group.name} (Group)`);
                                                                                    });
                                                                                }

                                                                                const displayProperties = selectedPropertyNames.length > 0
                                                                                    ? selectedPropertyNames.join(", ")
                                                                                    : (component.selectedProperty || "-");

                                                                                return (
                                                                                    <tr key={`group-component-${report._id}-${index}`} className="hover:bg-gray-50">
                                                                                        <td className="px-2 md:px-4 py-2 md:py-3 whitespace-nowrap text-xs md:text-sm font-medium text-gray-900 truncate max-w-[120px] md:max-w-none">
                                                                                            {component.name}
                                                                                        </td>
                                                                                        <td className="px-2 md:px-4 py-2 md:py-3 whitespace-nowrap text-xs md:text-sm text-right text-gray-600">
                                                                                            {report.currency}{component.cost.toFixed(2)}
                                                                                        </td>
                                                                                        <td className="px-2 md:px-4 py-2 md:py-3 whitespace-nowrap text-xs md:text-sm text-right text-gray-600">
                                                                                            {component.volume} {report.volumeUnit}
                                                                                        </td>
                                                                                        <td className="px-2 md:px-4 py-2 md:py-3 whitespace-nowrap text-xs md:text-sm text-right text-gray-600">
                                                                                            {component.percentage?.toFixed(1)}%
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            })}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    )
}

export default ComponentsPage;